import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useOptionalNavigation } from '../contexts/NavigationContext';
import { DataService } from '../services/dataService';
import { ContextualChatButton } from '../components/ContextualChatButton';
import { ExerciseCard, BaseExercise } from '../components/ExerciseCard';
import { WorkoutCategorizationService } from '../services/workoutCategorizationService';
import { useProfile } from '../contexts/ProfileContext';
import { ActivityDetailExplorer } from '../components/ActivityDetailExplorer';
import { useActivityMatching } from '../hooks/useActivityMatching';
import { ActivityData, ActivityMatchSuggestion } from '../types/DataTypes';
import { WorkoutMatchingService } from '../services/workoutMatchingService';
import { UnifiedWorkoutTimer } from '../components/UnifiedWorkoutTimer';
import { WorkoutSessionResults } from '../components/WorkoutSessionResults';
import { WorkoutWeatherDetail } from '../components/WorkoutCardWeather';
import { WatchPlatformBadge } from '../components/badges/SourceBadges';
import { storage } from '../services/storage';
import { getCurrentUserId } from '../utils/userStorage';
import { CmpBootomnavCoach as ProperNavCoach } from '../anima/Navigation Component/src/components/CmpBootomnavCoach/CmpBootomnavCoach.jsx';

import { WorkoutHistoryDisplay } from '../components/WorkoutHistoryDisplay';
import { HowItWentSection } from '../components/HowItWentSection';
import { DataSourceBadges } from '../components/badges/DataSourceBadges';
import { PushedToDeviceBadge } from '../components/badges/PushedToDeviceBadge';
import { PoweredByAttribution } from '../components/badges/PoweredByAttribution';
import { shouldShowPushStatus, shouldShowDataSources, getDataSources, getExternalSources } from '../utils/attributionHelpers';
import { ExpandableComponentMobility } from '../components/ExpandableComponentMobility'; // 🆕 PHASE 6 PART 1
import { PrimaryMetricsGrid } from '../components/PrimaryMetricsGrid';
import { PostCompletionMetricsGrid } from '../components/PostCompletionMetricsGrid';
import { CollapsibleRunPhase } from '../components/CollapsibleRunPhase'; // 🆕 JAN 29, 2026: Collapsible phase display
import { groupRepeatingPhases } from '../utils/phaseGrouping'; // 🆕 JAN 31, 2026: Smart phase grouping
// getEquipmentIcon removed — equipment now uses simple bullet list (Task 9)
import { ExpandableCoachingNotes } from '../components/ExpandableCoachingNotes';
import { ExpandablePhaseSection } from '../components/ExpandablePhaseSection';
import { WorkoutIcon, getWorkoutIconType, getCategoryColor } from '../utils/workoutIconMapper';
import { HeartRateZonesDisplayPre, shouldShowHRZones } from '../components/HeartRateZonesDisplayPre';
import { ExpandableHIITBreakdown } from '../components/ExpandableHIITBreakdown';
import { ExpandableExercises } from '../components/ExpandableExercises';
import { ManualCompletionModal } from '../components/ManualCompletionModal';
import { SessionDataAdapter } from '../services/sessionDataAdapter';
import { 
  hasPhases, 
  hasSplits, 
  hasElevationData, 
  hasHeartRateData, 
  hasPowerOrCadenceData, 
  hasSegments,
  hasExercises,
  hasHIITData,
  hasGPSData
} from '../utils/dataRichnessHelpers';
import { ExpandablePhaseBreakdown } from '../components/ExpandablePhaseBreakdown';
import { ExpandableSplits } from '../components/ExpandableSplits';
import { ExpandableElevation } from '../components/ExpandableElevation';
import { ExpandableHeartRateZones } from '../components/ExpandableHeartRateZones';
import { ExpandableGPSRouteMap } from '../components/ExpandableGPSRouteMap';
import { ExpandablePowerCadence } from '../components/ExpandablePowerCadence';
import { ExpandableSegments } from '../components/ExpandableSegments';
import { API_BASE_URL } from '../utils/api';

// Cross-platform dev check (Hermes-safe: no import.meta)
const isDev = typeof __DEV__ !== 'undefined' ? __DEV__ : (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development');

// Import the detailed workout content interfaces
interface ExerciseStep {
  id: string;
  name: string;
  description: string;
  duration?: string;
  reps?: string;
  sets?: string;
  rest?: string;
  images?: string[];
  form_cues: string[];
  safety_notes?: string[];
  circuit_info?: {
    circuit_number: number;
    round_number?: number;
    exercise_in_circuit: number;
    total_exercises_in_circuit: number;
    total_rounds?: number;
    is_circuit_start?: boolean;
    is_round_end?: boolean;
    is_circuit_end?: boolean;
    is_final_round?: boolean;
  };
}

interface WorkoutBriefing {
  workout_id: string;
  introduction: string;
  equipment_needed: string[];
  warm_up_guidance: string;
  cool_down_guidance: string;
  exercise_steps: ExerciseStep[];
  progression_notes: string;
  modification_options: string[];
  estimated_duration: number;
  difficulty_level: string;
  target_muscles: string[];
}

interface WorkoutDetailProps {
  workoutId?: string; // Optional when rendered by Route; id comes from useParams()
}

// ═══════════════════════════════════════════════════════════════════════════
// 🎯 WORKOUT PHASE GROUPING HELPERS (Jan 5, 2026)
// 
// Purpose: Detect and group repeating interval patterns for compact display
// Example: 6x(800m + 90s recovery) → Show once with "6x" prefix
// ═══════════════════════════════════════════════════════════════════════════

interface Phase {
  phase_type?: string;
  phase_name?: string;
  distance_meters?: number;
  distance_km?: number;
  duration_seconds?: number;
  duration_minutes?: number;
  target_pace_min_per_km?: string;
  target_pace?: string;
  target_hr_min?: number;
  target_hr_max?: number;
  instructions?: string;
  description?: string;
}

interface SinglePhaseGroup {
  type: 'single';
  phase: Phase;
}

interface RepeatedPhaseGroup {
  type: 'repeated';
  repeatCount: number;
  pattern: Phase[];
}

type GroupedPhase = SinglePhaseGroup | RepeatedPhaseGroup;

/**
 * Check if two phases match (same structure, different numbering)
 */
function phasesMatch(p1: Phase, p2: Phase): boolean {
  // Same phase type required
  if (p1.phase_type !== p2.phase_type) return false;
  
  // Compare distance (within 5% tolerance)
  if (p1.distance_meters && p2.distance_meters) {
    const diff = Math.abs(p1.distance_meters - p2.distance_meters);
    if (diff / p1.distance_meters > 0.05) return false;
  }
  if (p1.distance_km && p2.distance_km) {
    const diff = Math.abs(p1.distance_km - p2.distance_km);
    if (diff / p1.distance_km > 0.05) return false;
  }
  
  // Compare duration (within 5% tolerance)
  if (p1.duration_seconds && p2.duration_seconds) {
    const diff = Math.abs(p1.duration_seconds - p2.duration_seconds);
    if (diff / p1.duration_seconds > 0.05) return false;
  }
  if (p1.duration_minutes && p2.duration_minutes) {
    const diff = Math.abs(p1.duration_minutes - p2.duration_minutes);
    if (diff / p1.duration_minutes > 0.05) return false;
  }
  
  // Same target pace
  if (p1.target_pace_min_per_km !== p2.target_pace_min_per_km) return false;
  if (p1.target_pace !== p2.target_pace) return false;
  
  // Same target HR zone
  if (p1.target_hr_min !== p2.target_hr_min) return false;
  if (p1.target_hr_max !== p2.target_hr_max) return false;
  
  return true;
}

/**
 * Try to detect a repeating pattern of given length starting at index
 */
function tryPatternLength(
  phases: Phase[], 
  startIdx: number, 
  patternLength: number
): { repeatCount: number; pattern: Phase[]; totalPhasesConsumed: number } | null {
  
  if (startIdx + patternLength > phases.length) return null;
  
  // Extract candidate pattern
  const candidatePattern = phases.slice(startIdx, startIdx + patternLength);
  
  // Check how many times this pattern repeats
  let repeatCount = 1;
  let checkIdx = startIdx + patternLength;
  
  while (checkIdx + patternLength <= phases.length) {
    const nextPattern = phases.slice(checkIdx, checkIdx + patternLength);
    
    // Check if all phases in pattern match
    let allMatch = true;
    for (let i = 0; i < patternLength; i++) {
      if (!phasesMatch(candidatePattern[i], nextPattern[i])) {
        allMatch = false;
        break;
      }
    }
    
    if (allMatch) {
      repeatCount++;
      checkIdx += patternLength;
    } else {
      break;
    }
  }
  
  // Only consider it a pattern if it repeats 3+ times
  if (repeatCount >= 3) {
    return {
      repeatCount,
      pattern: candidatePattern,
      totalPhasesConsumed: repeatCount * patternLength
    };
  }
  
  return null;
}

/**
 * Detect repeating interval patterns
 * Tries pattern lengths: 2 (interval+recovery), 3 (complex), 1 (standalone)
 */
function detectPattern(phases: Phase[], startIdx: number): { repeatCount: number; pattern: Phase[]; totalPhasesConsumed: number } | null {
  // Try pattern length 2 first (most common: interval + recovery)
  const pattern2 = tryPatternLength(phases, startIdx, 2);
  if (pattern2) return pattern2;
  
  // Try pattern length 3 (interval + recovery + drill)
  const pattern3 = tryPatternLength(phases, startIdx, 3);
  if (pattern3) return pattern3;
  
  // Try pattern length 1 (same interval repeated, no recovery)
  const pattern1 = tryPatternLength(phases, startIdx, 1);
  if (pattern1) return pattern1;
  
  return null;
}

/**
 * Group consecutive identical interval phases for compact display
 */
function groupPhases(phases: Phase[]): GroupedPhase[] {
  const grouped: GroupedPhase[] = [];
  let i = 0;
  
  while (i < phases.length) {
    const currentPhase = phases[i];
    const phaseType = currentPhase.phase_type?.toLowerCase() || '';
    
    // Never group warmup or cooldown
    if (phaseType === 'warmup' || phaseType === 'cooldown') {
      grouped.push({ type: 'single', phase: currentPhase });
      i++;
      continue;
    }
    
    // Try to find repeating pattern
    const pattern = detectPattern(phases, i);
    
    if (pattern) {
      // Found repeating pattern with 3+ repetitions
      grouped.push({
        type: 'repeated',
        repeatCount: pattern.repeatCount,
        pattern: pattern.pattern
      });
      i += pattern.totalPhasesConsumed;
    } else {
      // Single non-repeating phase
      grouped.push({ type: 'single', phase: currentPhase });
      i++;
    }
  }
  
  return grouped;
}

/**
 * Format phase name for display
 * activation → Activation, main_compound → Main Workout, accessory → Accessory
 */
function formatPhaseName(name: string): string {
  const normalized = (name || '').toLowerCase().trim();
  
  if (normalized.includes('main') && normalized.includes('compound')) {
    return 'Main Workout';
  }
  if (normalized.includes('activation')) {
    return 'Activation';
  }
  if (normalized.includes('accessory')) {
    return 'Accessory';
  }
  
  // Safety check: handle undefined/null/empty names
  if (!name || name.length === 0) {
    return 'Phase';
  }
  
  // Fallback: capitalize first letter
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/**
 * Outer shell: resolves the workout id and guards on it. It deliberately owns
 * no state and no effects.
 *
 * 2026-09-17 (hooks debt): the guard used to sit INSIDE the component, above
 * TWENTY-NINE hooks — so the "Hooks MUST be called before any conditional
 * returns" note described four of them and was wrong about the rest. The
 * guard exists precisely because `workoutId` CAN be missing on one render and
 * present on the next (StrictMode reconciliation, params not ready yet), and a
 * component that renders N hooks once and N+29 the next makes React throw
 * "Rendered more hooks than during the previous render" — the DebriefButton
 * crash class. Splitting the guard from the hooks removes the class: every
 * hook in WorkoutDetailLoaded runs on every render of WorkoutDetailLoaded.
 */
export const WorkoutDetail: React.FC<WorkoutDetailProps> = (props) => {
  // 🔧 STRICTMODE FIX (Jan 21, 2026): Handle React reconciliation passes
  // Root cause: StrictMode in main.tsx causes React to rebuild component tree
  // During reconciliation, props can be temporarily undefined
  // Solution: Skip render on bad pass (React will re-render with correct props)
  const { id: paramId } = useLocalSearchParams<{ id: string }>();
  const workoutId = (props?.workoutId ?? paramId) || undefined;

  // If no workoutId during reconciliation pass or before params ready, skip this render
  if (!workoutId) {
    return null;
  }

  return <WorkoutDetailLoaded workoutId={workoutId} />;
};

const WorkoutDetailLoaded: React.FC<{ workoutId: string }> = ({ workoutId }) => {
  const router = useRouter();

  // Defensive navigation hook usage (matches other detail pages)
  // 2026-09-17: was `useNavigation?.() ?? null` — a hook behind an optional
  // call is a Rules-of-Hooks violation, and it still THREW when no provider was
  // mounted. useOptionalNavigation() is called unconditionally and returns null.
  const navigationContext = useOptionalNavigation();

  const { profile } = useProfile();

  const goBack = navigationContext?.goBack || (() => router.back());
  const navigateToDetail = navigationContext?.navigateToDetail ||
    ((type: any, id: string, source?: string) => router.push(`/${type}/${id}`));
  const getCurrentSource = navigationContext?.getCurrentSource || (() => 'unknown');

  // 🔧 FIX FEB 4, 2026: Use NavigationContext goBack() ONLY for stateful overlay
  const handleSmartBack = () => {
    console.log('🔙 WorkoutDetail: handleSmartBack called');
    const source = getCurrentSource();
    console.log('🔙 WorkoutDetail: source =', source);

    // 🔧 CRITICAL: MyPath uses stateful overlay pattern (line 1976)
    // Must use goBack() to reset navigationState, NOT navigate()
    if (navigationContext) {
      console.log('🔙 WorkoutDetail: Using NavigationContext.goBack() for stateful overlay');

      // Dispatch event to MyPath to set viewMode/entityFilter
      if (source === 'today' || source === 'browse-goals' || source === 'browse-plans' || source === 'browse-sessions') {
        console.log('🔙 WorkoutDetail: Dispatching navigateBackToMyPath event for source:', source);
        window.dispatchEvent(new CustomEvent('navigateBackToMyPath', {
          detail: { source }
        }));
      }

      // Reset NavigationContext state (closes detail overlay)
      navigationContext.goBack();
      console.log('🔙 WorkoutDetail: NavigationContext.goBack() called - detail should close');
    } else {
      console.log('🔙 WorkoutDetail: No NavigationContext - falling back to router.back()');
      // Fallback for route-based navigation
      router.back();
    }
  };
  const [workout, setWorkout] = useState<any>(null);
  const [exercises, setExercises] = useState<BaseExercise[]>([]);
  const [expandedExercise, setExpandedExercise] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [workoutBriefing, setWorkoutBriefing] = useState<WorkoutBriefing | null>(null);
  const [matchedActivity, setMatchedActivity] = useState<ActivityData | null>(null);
  const [showActivityExplorer, setShowActivityExplorer] = useState(false);
  const [componentMobility, setComponentMobility] = useState<any[]>([]); // 🆕 PHASE 6 PART 1: Warmup/cooldown mobility
  
  // Session tracking state
  const [showUnifiedTimer, setShowUnifiedTimer] = useState(false);
  const [sessionData, setSessionData] = useState<any>(null);
  const [showSessionResults, setShowSessionResults] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);

  // 🆕 PHASE 3: HR Zones state
  const [userHrZones, setUserHrZones] = useState<any>(null);

  // Sprint 3 Task 3.1: Manual completion modal state
  const [showManualCompletionModal, setShowManualCompletionModal] = useState(false);

  // 🆕 POST-COMPLETION: Activity data and unified session state
  const [activityData, setActivityData] = useState<any>(null);
  const [unifiedSession, setUnifiedSession] = useState<any>(null);
  const [loadingActivityData, setLoadingActivityData] = useState(false);
  // P3-15: Track whether we're in a "data arriving" patience phase
  const [dataArrivingPhase, setDataArrivingPhase] = useState<'idle' | 'polling' | 'waiting' | 'unavailable'>('idle');

  // ── activity_id polling (mirrors native WorkoutDetail pattern) ──
  // After timer completion, the backend pipeline generates activity_id asynchronously.
  // Poll GET /api/workouts/{id} until activity_id appears, then update workout state.
  const activityIdPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopActivityIdPolling = useCallback(() => {
    if (activityIdPollRef.current !== null) {
      clearTimeout(activityIdPollRef.current);
      activityIdPollRef.current = null;
    }
  }, []);

  const startActivityIdPolling = useCallback(
    async () => {
      // Don't double-start
      if (activityIdPollRef.current !== null) return;

      const token = await storage.getItem('jwt_token');
      if (!token) return;

      // Backoff schedule: 3s x 5 (fast), 5s x 5 (moderate), 10s x 6 (patient) = 100s total
      const POLL_INTERVALS = [
        3000, 3000, 3000, 3000, 3000,
        5000, 5000, 5000, 5000, 5000,
        10000, 10000, 10000, 10000, 10000, 10000
      ];
      let attemptIndex = 0;

      const scheduleNext = () => {
        if (attemptIndex >= POLL_INTERVALS.length) {
          stopActivityIdPolling();
          return;
        }

        const delay = POLL_INTERVALS[attemptIndex];
        attemptIndex += 1;

        activityIdPollRef.current = setTimeout(async () => {
          try {
            const res = await fetch(`${API_BASE_URL}/api/workouts/${workoutId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) { scheduleNext(); return; }

            const data = await res.json();
            if (!data.activity_id) { scheduleNext(); return; }

            // activity_id arrived — stop polling and update workout state
            stopActivityIdPolling();
            console.log('✅ activity_id polling: Found', data.activity_id);
            setWorkout((prev: any) => prev ? { ...prev, activity_id: data.activity_id } : prev);
          } catch (pollErr) {
            console.warn('⚠️ activity_id polling: fetch failed:', pollErr);
            scheduleNext();
          }
        }, delay) as unknown as ReturnType<typeof setTimeout>;
      };

      scheduleNext();
    },
    [workoutId, stopActivityIdPolling],
  );

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      stopActivityIdPolling();
    };
  }, [stopActivityIdPolling]);

  // 🔧 FIX (Jan 21, 2026): Define workoutType at component level for use throughout
  // Used by ContextualChatButton metadata and other components
  // 🔧 CRITICAL FIX (Jan 21, 2026): Ensure string type, handle empty objects
  const workoutType = typeof workout?.type === 'string' ? workout.type : 
                      typeof workout?.workout_type === 'string' ? workout.workout_type :
                      (workout?.type?.toString() || workout?.workout_type?.toString() || 'cardio');

  // 🆕 PHASE 3: Helper function to convert BPM range to zone name
  const getHrZoneName = (minBpm: number, maxBpm: number): string | null => {
    if (!userHrZones) return null;

    const midpoint = Math.round((minBpm + maxBpm) / 2);
    
    // Check which zone this BPM range falls into
    if (midpoint >= userHrZones.zone1Min && midpoint <= userHrZones.zone1Max) {
      return userHrZones.source === 'garmin' ? 'Zone 1 Recovery' : 'Zone 1 Recovery';
    }
    if (midpoint >= userHrZones.zone2Min && midpoint <= userHrZones.zone2Max) {
      return userHrZones.source === 'garmin' ? 'Zone 2 Aerobic' : 'Zone 2 Aerobic';
    }
    if (midpoint >= userHrZones.zone3Min && midpoint <= userHrZones.zone3Max) {
      return userHrZones.source === 'garmin' ? 'Zone 3 Tempo' : 'Zone 3 Tempo';
    }
    if (midpoint >= userHrZones.zone4Min && midpoint <= userHrZones.zone4Max) {
      return userHrZones.source === 'garmin' ? 'Zone 4 Threshold' : 'Zone 4 Threshold';
    }
    if (midpoint >= userHrZones.zone5Min && midpoint <= userHrZones.zone5Max) {
      return userHrZones.source === 'garmin' ? 'Zone 5 Max' : 'Zone 5 Max Effort';
    }
    
    return null; // No zone match
  };

  // 🔧 ISSUE 2 FIX: Helper to determine if workout is outdoor (needs weather)
  const isOutdoorWorkout = (workout: any): boolean => {
    if (!workout) return false;
    
    const workoutType = workout.type || workout.workout_type || '';
    const activityType = workout.activityType || '';
    const location = workout.location?.toLowerCase() || '';
    
    // Explicit location indicators
    if (location.includes('outdoor') || location.includes('outside')) return true;
    if (location.includes('indoor') || location.includes('gym') || location.includes('home')) return false;
    
    // Activity type detection - outdoor activities
    const outdoorActivities = ['running', 'cycling', 'biking', 'swimming-open', 'hiking', 'walking'];
    if (outdoorActivities.some(activity => workoutType.toLowerCase().includes(activity) || activityType.toLowerCase().includes(activity))) {
      return true;
    }
    
    // Explicit cardio check (most cardio is outdoor unless specified)
    if (activityType === 'cardio' && workoutType.toLowerCase().includes('running')) {
      return true;
    }
    
    // Indoor activities (default for strength, HIIT, mobility)
    if (activityType === 'strength' || activityType === 'mobility' || 
        workoutType.toLowerCase().includes('strength') || 
        workoutType.toLowerCase().includes('weights') ||
        workoutType.toLowerCase().includes('yoga') ||
        workoutType.toLowerCase().includes('pilates')) {
      return false;
    }
    
    // Default: assume indoor to avoid showing weather unnecessarily
    return false;
  };

  // Initialize activity matching hook
  const {
    notifications: activityMatchingNotifications,
    processNewActivity,
    rejectActivityMatch,
    clearAllNotifications,
    isProcessing: activityMatchingIsProcessing
  } = useActivityMatching({ 
    userId: 'demo-user', // Would be from auth context
    autoMatchThreshold: 0.85,
    enableNotifications: true 
  });

  // 🆕 STRENGTH-AUDIT-7: Calculate actual duration from phases (including rest times)
  const calculateActualDuration = (workout: any): number => {
    const phases = workout.workout_data?.phases || workout.phases || [];
    if (phases.length === 0) {
      return workout.duration || workout.duration_minutes || 0;
    }
    
    // Calculate total duration from all phases
    let totalSeconds = 0;
    phases.forEach((phase: any) => {
      // Use phase duration_seconds if available
      if (phase.duration_seconds) {
        totalSeconds += phase.duration_seconds;
      } else if (phase.duration_minutes) {
        totalSeconds += phase.duration_minutes * 60;
      } else {
        // Calculate from exercises if no phase duration
        // 🔧 FIX: Check BOTH exercises and exercises_json fields
        const phaseExercises = phase.exercises || phase.exercises_json || [];
        if (Array.isArray(phaseExercises) && phaseExercises.length > 0) {
          phaseExercises.forEach((ex: any) => {
            const sets = ex.sets || 1;
            const repsPerSet = ex.reps || ex.duration_seconds || 30;
            const restSeconds = phase.rest_seconds || ex.rest_seconds || 60;
            // Estimate 2-3 seconds per rep, plus rest between sets
            const workTime = sets * (typeof repsPerSet === 'number' ? repsPerSet * 2 : 30);
            const restTime = (sets - 1) * restSeconds;
            totalSeconds += workTime + restTime;
          });
        }
      }
    });
    
    // Convert to minutes, round up
    return Math.ceil(totalSeconds / 60);
  };

  // Helper functions - moved before useEffect to avoid React Hooks violation
  const getWorkoutObjective = (type: string): string => {
    const objectives = {
      'cardio': 'improve cardiovascular endurance and burn calories efficiently',
      'strength': 'build muscle strength, power, and definition',
      'hiit': 'maximize calorie burn and improve both strength and cardio in minimal time',
      'flexibility': 'enhance mobility, reduce tension, and improvement quality',
      'sports': 'develop sport-specific skills and athletic performance',
      'recovery': 'promote active recovery and muscle regeneration'
    };
    return objectives[type] || 'improve overall fitness and well-being';
  };

  const getTargetMuscles = (type: string): string[] => {
    const muscleGroups = {
      'cardio': ['Cardiovascular system', 'Legs', 'Core'],
      'strength': ['Full body', 'Major muscle groups'],
      'hiit': ['Full body', 'Cardiovascular system', 'Core'],
      'flexibility': ['All muscle groups', 'Connective tissue'],
      'sports': ['Sport-specific muscles', 'Coordination', 'Agility'],
      'recovery': ['All muscle groups', 'Nervous system']
    };
    return muscleGroups[type] || ['Full body'];
  };

  // Generate enhanced exercise steps - moved before useEffect
  // Helper to determine workout layout type (same logic as WorkoutHistoryDisplay)
  const getWorkoutLayout = (workout: any): 'interval' | 'cardio' | 'strength' | 'mobility' | 'workout' => {
    const workoutType = workout.type || workout.workout_type; // "running-tempo", "running-intervals", etc.
    const activityType = workout.activityType; // "cardio", "strength", etc.
    const phases = workout.phases || [];
    
    // 🔧 AUDIT FIX JAN 19: Check activityType FIRST, then workout_type keywords, THEN phase count
    // Easy base runs with 3 phases (warmup/main/cooldown) should be 'cardio', not 'interval'
    
    // Priority 1: Check activity type first
    if (activityType === 'strength' || workoutType?.includes('strength') || 
        workoutType?.includes('weights') || workoutType?.includes('bodyweight') ||
        workoutType?.includes('resistance')) {
      return 'strength';
    }
    
    if (activityType === 'mobility' || workoutType?.includes('mobility') || 
        workoutType?.includes('stretch') || workoutType?.includes('yoga')) {
      return 'mobility';
    }
    
    // Priority 2A: Check for EXPLICIT interval/structured keywords (BEFORE phase count!)
    // These are TRUE interval workouts (tempo, fartlek, progression, etc.)
    if (workoutType?.includes('interval') || workoutType?.includes('tempo') || 
        workoutType?.includes('hiit') || workoutType?.includes('circuit') ||
        workoutType?.includes('fartlek') || workoutType?.includes('progression')) {
      return 'interval';
    }
    
    // Priority 2B: Check for base/easy/recovery keywords → ALWAYS 'cardio' (even with phases!)
    // Easy base runs with warmup/main/cooldown should NOT be classified as intervals
    if (workoutType?.includes('base') || workoutType?.includes('easy') ||
        workoutType?.includes('recovery') || workoutType?.includes('long-run') ||
        workoutType?.includes('long run') || workoutType?.includes('steady')) {
      return 'cardio';
    }
    
    // Priority 3: Check phase structure (>1 phase = structured CARDIO workout ONLY for unknown types)
    // This catches tempo/interval workouts that didn't match keywords above
    if (phases.length > 1 && (activityType === 'cardio' || workoutType?.includes('running') || 
        workoutType?.includes('cycling') || workoutType?.includes('swimming'))) {
      return 'interval';
    }
    
    // Priority 4: Check if it's cardio (single-phase or general cardio)
    if (activityType === 'cardio' || workoutType?.includes('running') || 
        workoutType?.includes('cycling') || workoutType?.includes('swimming') ||
        workoutType?.includes('walking')) {
      return 'cardio';
    }
    
    // 🆕 JAN 2026: Enhanced handling for device-synced workouts with minimal structure
    // If workout is completed and has no phases/exercises, classify by activity type
    const hasMinimalStructure = !phases.length && (!Array.isArray(workout.exercises) || !workout.exercises.length);
    if (hasMinimalStructure && workout.status === 'completed') {
      // Device-synced workout - classify by activity type
      if (activityType === 'cardio' || 
          workoutType?.includes('running') || 
          workoutType?.includes('walking') || 
          workoutType?.includes('cycling') ||
          workoutType?.includes('swimming')) {
        return 'cardio';
      }
      
      if (activityType === 'strength' || workoutType?.includes('strength')) {
        return 'strength';
      }
      
      if (activityType === 'mobility') {
        return 'mobility';
      }
    }
    
    return 'workout'; // Default
  };

  const generateEnhancedExerciseSteps = (workout: any): ExerciseStep[] => {
    // Only generate mock exercises for STRENGTH workouts
    // For cardio/interval workouts, return empty array (use phases instead)
    const layout = getWorkoutLayout(workout);
    if (layout === 'interval' || layout === 'cardio') {
      return []; // Don't show exercises for cardio workouts
    }
    
    const baseExercises = {
      'bodyweight-strength': [
        {
          id: 'push-ups',
          name: 'Push-ups',
          description: 'Classic upper body exercise targeting chest, shoulders, and triceps',
          duration: '45',
          reps: '8-12',
          sets: '3',
          rest: '60',
          images: [
            'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
          ],
          form_cues: [
            'Keep your body in a straight line',
            'Lower chest to floor level',
            'Push through palms to return to start',
            'Engage core throughout movement'
          ],
          safety_notes: ['Avoid sagging hips', 'Don\'t lock elbows at top']
        },
        {
          id: 'squats',
          name: 'Bodyweight Squats',
          description: 'Fundamental lower body movement pattern',
          duration: '60',
          reps: '12-15',
          sets: '3',
          rest: '45',
          images: [
            'https://images.unsplash.com/photo-1574680096145-d05b474e2155?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80'
          ],
          form_cues: [
            'Feet shoulder-width apart',
            'Lower hips back and down',
            'Keep chest up and knees tracking over toes',
            'Drive through heels to stand'
          ],
          safety_notes: ['Don\'t let knees cave inward', 'Maintain neutral spine']
        },
        {
          id: 'plank',
          name: 'Plank Hold',
          description: 'Isometric core strengthening exercise',
          duration: '30-60',
          reps: '1',
          sets: '3',
          rest: '60',
          images: [
            'https://images.unsplash.com/photo-1518611012118-696072aa579a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
          ],
          form_cues: [
            'Maintain straight line from head to heels',
            'Engage core and glutes',
            'Breathe normally throughout hold',
            'Keep shoulders over elbows'
          ],
          safety_notes: ['Avoid sagging or piking hips', 'Stop if form breaks down']
        }
      ],
      'circuit-training': [
        // Circuit 1 - Round 1
        {
          id: 'circuit-1-round-1-pushups',
          name: 'Push-ups',
          description: 'Circuit 1 - Exercise 1 of 3 (Round 1)',
          duration: '45',
          reps: '10',
          sets: '1',
          rest: '15',
          circuit_info: {
            circuit_number: 1,
            round_number: 1,
            exercise_in_circuit: 1,
            total_exercises_in_circuit: 3,
            total_rounds: 3,
            is_circuit_start: true
          },
          images: [
            'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
          ],
          form_cues: [
            'Keep your body in a straight line',
            'Lower chest to floor level',
            'Push through palms to return to start'
          ]
        },
        {
          id: 'circuit-1-round-1-squats',
          name: 'Bodyweight Squats',
          description: 'Circuit 1 - Exercise 2 of 3 (Round 1)',
          duration: '45',
          reps: '15',
          sets: '1',
          rest: '15',
          circuit_info: {
            circuit_number: 1,
            round_number: 1,
            exercise_in_circuit: 2,
            total_exercises_in_circuit: 3,
            total_rounds: 3
          },
          images: [
            'https://images.unsplash.com/photo-1574680096145-d05b474e2155?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80'
          ],
          form_cues: [
            'Feet shoulder-width apart',
            'Lower hips back and down',
            'Drive through heels to stand'
          ]
        },
        {
          id: 'circuit-1-round-1-burpees',
          name: 'Burpees',
          description: 'Circuit 1 - Exercise 3 of 3 (Round 1)',
          duration: '45',
          reps: '8',
          sets: '1',
          rest: '60', // Rest between rounds
          circuit_info: {
            circuit_number: 1,
            round_number: 1,
            exercise_in_circuit: 3,
            total_exercises_in_circuit: 3,
            total_rounds: 3,
            is_round_end: true
          },
          images: [
            'https://images.unsplash.com/photo-1549476464-37392f717541?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2187&q=80'
          ],
          form_cues: [
            'Drop to squat position',
            'Jump feet back to plank',
            'Jump feet forward and stand',
            'Add jump at top if able'
          ]
        },
        // Circuit 1 - Round 2
        {
          id: 'circuit-1-round-2-pushups',
          name: 'Push-ups',
          description: 'Circuit 1 - Exercise 1 of 3 (Round 2)',
          duration: '45',
          reps: '10',
          sets: '1',
          rest: '15',
          circuit_info: {
            circuit_number: 1,
            round_number: 2,
            exercise_in_circuit: 1,
            total_exercises_in_circuit: 3,
            total_rounds: 3
          },
          images: [
            'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
          ],
          form_cues: [
            'Keep your body in a straight line',
            'Lower chest to floor level',
            'Push through palms to return to start'
          ]
        },
        {
          id: 'circuit-1-round-2-squats',
          name: 'Bodyweight Squats',
          description: 'Circuit 1 - Exercise 2 of 3 (Round 2)',
          duration: '45',
          reps: '15',
          sets: '1',
          rest: '15',
          circuit_info: {
            circuit_number: 1,
            round_number: 2,
            exercise_in_circuit: 2,
            total_exercises_in_circuit: 3,
            total_rounds: 3
          },
          images: [
            'https://images.unsplash.com/photo-1574680096145-d05b474e2155?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80'
          ],
          form_cues: [
            'Feet shoulder-width apart',
            'Lower hips back and down',
            'Drive through heels to stand'
          ]
        },
        {
          id: 'circuit-1-round-2-burpees',
          name: 'Burpees',
          description: 'Circuit 1 - Exercise 3 of 3 (Round 2)',
          duration: '45',
          reps: '8',
          sets: '1',  
          rest: '60',
          circuit_info: {
            circuit_number: 1,
            round_number: 2,
            exercise_in_circuit: 3,
            total_exercises_in_circuit: 3,
            total_rounds: 3,
            is_round_end: true
          },
          images: [
            'https://images.unsplash.com/photo-1549476464-37392f717541?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2187&q=80'
          ],
          form_cues: [
            'Drop to squat position',
            'Jump feet back to plank',
            'Jump feet forward and stand',
            'Add jump at top if able'
          ]
        },
        // Circuit 1 - Round 3 (Final)
        {
          id: 'circuit-1-round-3-pushups',
          name: 'Push-ups',
          description: 'Circuit 1 - Exercise 1 of 3 (Final Round)',
          duration: '45',
          reps: '10',
          sets: '1',
          rest: '15',
          circuit_info: {
            circuit_number: 1,
            round_number: 3,
            exercise_in_circuit: 1,
            total_exercises_in_circuit: 3,
            total_rounds: 3,
            is_final_round: true
          },
          images: [
            'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
          ],
          form_cues: [
            'Keep your body in a straight line',
            'Lower chest to floor level',
            'Push through palms to return to start'
          ]
        },
        {
          id: 'circuit-1-round-3-squats',
          name: 'Bodyweight Squats',
          description: 'Circuit 1 - Exercise 2 of 3 (Final Round)',
          duration: '45',
          reps: '15',
          sets: '1',
          rest: '15',
          circuit_info: {
            circuit_number: 1,
            round_number: 3,
            exercise_in_circuit: 2,
            total_exercises_in_circuit: 3,
            total_rounds: 3,
            is_final_round: true
          },
          images: [
            'https://images.unsplash.com/photo-1574680096145-d05b474e2155?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80'
          ],
          form_cues: [
            'Feet shoulder-width apart',
            'Lower hips back and down',
            'Drive through heels to stand'
          ]
        },
        {
          id: 'circuit-1-round-3-burpees',
          name: 'Burpees',
          description: 'Circuit 1 - Exercise 3 of 3 (Final Round)',
          duration: '45',
          reps: '8',
          sets: '1',
          rest: '120', // Longer rest after circuit completion
          circuit_info: {
            circuit_number: 1,
            round_number: 3,
            exercise_in_circuit: 3,
            total_exercises_in_circuit: 3,
            total_rounds: 3,
            is_round_end: true,
            is_circuit_end: true,
            is_final_round: true
          },
          images: [
            'https://images.unsplash.com/photo-1549476464-37392f717541?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2187&q=80'
          ],
          form_cues: [
            'Drop to squat position',
            'Jump feet back to plank',
            'Jump feet forward and stand',
            'Add jump at top if able'
          ]
        }
      ],
      'flexibility': [
        {
          id: 'forward-fold',
          name: 'Standing Forward Fold',
          description: 'Hamstring and calf stretch',
          duration: '30-45',
          reps: '1',
          sets: '2',
          rest: '15',
          images: [
            'https://images.unsplash.com/photo-1506629905607-c19ed1e3bb72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
          ],
          form_cues: [
            'Hinge at hips, not waist',
            'Keep slight bend in knees',
            'Let arms hang naturally',
            'Breathe deeply into stretch'
          ],
          safety_notes: ['Don\'t force the stretch', 'Come up slowly']
        }
      ]
    };

    // Use circuit training for specific workout types
    if (workout.type === 'hiit' || workout.title?.toLowerCase().includes('circuit')) {
      return baseExercises['circuit-training'];
    }

    return baseExercises[workout.type] || baseExercises['bodyweight-strength'];
  };

  // Check for existing matched activity on load
  useEffect(() => {
    if (workout) {
      // Check if this workout has an associated activity
      const activity = DataService.getMatchedActivity(workout.id);
      if (activity) {
        setMatchedActivity(activity);
        setShowActivityExplorer(true);
      }
    }
  }, [workout]);

  // Simulate activity detection (in real app, this would be from Strava webhooks)
  useEffect(() => {
    if (workout && workout.state === 'completed' && !matchedActivity) {
      // Simulate finding a potential activity match
      setTimeout(() => {
        const simulatedActivity = createSimulatedActivity(workout);
        if (simulatedActivity) {
          processNewActivity(simulatedActivity, [workout]);
        }
      }, 2000);
    }
  }, [workout, matchedActivity, processNewActivity]);

  // Handle activity match acceptance
  const handleAcceptMatch = async (suggestion: ActivityMatchSuggestion) => {
    // Process the match acceptance with auto-completion
    const result = WorkoutMatchingService.processMatchAcceptance(
      suggestion.activity.id,
      suggestion.workout.id,
      suggestion
    );
    
    if (result.success) {
      // Update activity status to matched
      setMatchedActivity(prev => prev ? {
        ...prev,
        workout_id: suggestion.workout.id,
        status: 'matched',
        match_confidence: suggestion.confidence,
        manual_match: true,
        updated_at: new Date()
      } : null);
      
      // Auto-completion notification
      if (result.autoCompleted) {
        // 🔧 REACT ERROR #31 FIX (Jan 22, 2026): Use React state update instead of window.reload
        // window.location.reload() breaks ALL React hooks by unmounting the entire app!
        // Instead: Clear cache and refetch data to update UI
        DataService.clearCache();
        const freshData = await DataService.getAllData();
        const updatedWorkout = DataService.getWorkoutById(workoutId);
        if (updatedWorkout) {
          setWorkout(sanitizeDateObjects(updatedWorkout));
        }
      }
      
      // Clear notification
      clearAllNotifications();
    }
  };

  // Handle activity match rejection
  const handleRejectMatch = async (activityId: string) => {
    try {
      await rejectActivityMatch(activityId);
      console.log('📊 Standalone activity created');
    } catch (error) {
      console.error('Error rejecting match:', error);
    }
  };

  // Create simulated activity for demo purposes
  const createSimulatedActivity = (workout: any): ActivityData | null => {
    if (workout.type === 'cardio') {
      return {
        id: `activity_${Date.now()}`,
        external_id: `strava_${Date.now()}`,
        user_id: 'demo-user',
        start: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
        duration_sec: 2700, // 45 minutes
        distance_m: 8000, // 8km
        sport: 'run',
        name: `${String(workout.title || 'Workout')} - Completed`,
        avg_hr: 145,
        max_hr: 168,
        calories: 420,
        ascent_m: 120,
        training_load: 185,
        avg_cadence: 180,
        status: 'pending_match',
        created_at: new Date(),
        updated_at: new Date(),
        samples: [] // Would contain GPS and sensor data
      };
    }
    return null;
  };

  // Session handlers
  const handleStartSession = () => {
    // 🆕 FEB 4, 2026 (Multi-Stage): ALWAYS open UnifiedWorkoutTimer (handles warmup internally)
    // UnifiedWorkoutTimer now has multi-stage support:
    // - If componentMobility has warmup → Starts at warmup stage
    // - If no warmup → Starts at main workout stage
    // - If componentMobility has cooldown → Auto-advances to cooldown after main
    // - Never navigates to MobilityDetail pages (all internal)
    
    console.log('🏋️ START SESSION: Opening UnifiedWorkoutTimer with multi-stage support');
    console.log('   Component mobility:', componentMobility.length, 'routines');
    console.log('   Has warmup:', componentMobility.some((m: any) => m.title?.toLowerCase().includes('warm')));
    console.log('   Has cooldown:', componentMobility.some((m: any) => m.title?.toLowerCase().includes('cool')));
    
    setShowUnifiedTimer(true);
  };

  const handleSessionComplete = async (workoutSessionData: any) => {
    console.log('Session completed:', workoutSessionData);
    setSessionData(workoutSessionData);
    setShowUnifiedTimer(false);
    setShowSessionResults(true);
    
    // 🔥 CRITICAL FIX: Actually mark the workout complete and persist session data
    if (workout) {
      const result = await DataService.markWorkoutComplete(workout.id, undefined, workoutSessionData);
      if (result?.success) {
        // Update local state to show completion
        setWorkout(prev => prev ? {
          ...prev,
          status: 'completed',
          completedAt: new Date(),
          activity_id: result?.activity_id ?? prev?.activity_id
        } : null);

        // If activity_id not yet available, start polling for it
        if (!result?.activity_id) {
          console.log('[WorkoutDetail] Completed but no activity_id yet — starting poll');
          startActivityIdPolling();
        } else {
          console.log('[WorkoutDetail] activity_id available immediately:', result.activity_id);
        }

        // 🗄️ PERSIST SESSION DATA: Store the session data permanently
        // This ensures workout history is preserved for LLM and progress analysis
        const sessionRecord = {
          id: workoutSessionData.id,
          workoutId: workout.id,
          sessionData: workoutSessionData,
          completedAt: new Date().toISOString(),
          activityType: workout.type || 'strength'
        };
        
        // Store session for historical analysis
        const existingSessions = JSON.parse(await storage.getItem('workoutSessions') || '[]');
        existingSessions.push(sessionRecord);
        await storage.setItem('workoutSessions', JSON.stringify(existingSessions));
        
        // 🎯 CEDAR PROGRESS ANALYSIS: Analyze improvement and celebrate progress
        const progressAnalysis = DataService.analyzeActivityProgress(workout.id);
        
        // POST-WORKOUT CHAT: previously fetched the legacy
        // post-workout-completion endpoint to trigger Cedar's debrief.
        // That endpoint was removed 2026-05-04 (bypassed the unified
        // pipeline — no _is_major_activity gating, no debrief_started_at,
        // no race protection). Web is legacy per CLAUDE.md; native uses
        // the start-debrief endpoint via the shared <DebriefButton>
        // component on WorkoutDetail.native.tsx. Web users now complete
        // workouts without an immediate chat message — acceptable per
        // the legacy-web policy.

        if (progressAnalysis.shouldCelebrate && progressAnalysis.progressInsights.length > 0) {
          // Show progress celebration in session results
          setSessionData(prev => ({
            ...prev,
            progressInsights: progressAnalysis.progressInsights,
            shouldCelebrate: true,
            comparisonData: progressAnalysis.comparisonData
          }));
          
          // Log progress for Cedar chat context
          console.log('🎉 PROGRESS DETECTED:', progressAnalysis.progressInsights.join(' '));
        }
      }
    }
  };

  const handleSessionDiscard = () => {
    setShowUnifiedTimer(false);
  };

  const handleSessionExit = () => {
    setShowUnifiedTimer(false);
  };

  const handleCloseResults = () => {
    setShowSessionResults(false);
    setSessionData(null);
  };

  const handleViewActivity = () => {
    if (sessionData && sessionData.type === 'cardio') {
      // Convert session data to activity format for viewing
      const activityData: ActivityData = {
        id: sessionData.id,
        external_id: sessionData.id,
        user_id: 'demo-user',
        start: sessionData.startTime,
        duration_sec: sessionData.totalDuration,
        distance_m: sessionData.distance ? sessionData.distance * 1000 : 0,
        sport: String(workout?.title || '').toLowerCase().includes('run') ? 'run' : 'ride',
        name: `${String(workout?.title || 'Workout')} - Manual Session`,
        avg_hr: sessionData.heartRate?.avg,
        max_hr: sessionData.heartRate?.max,
        calories: sessionData.calories,
        status: 'standalone' as const,
        created_at: sessionData.startTime,
        updated_at: sessionData.endTime || new Date(),
        samples: sessionData.gpsData?.map((point: any, index: number) => ({
          id: `sample_${index}`,
          activity_id: sessionData.id,
          ts_offset_s: Math.floor((point.timestamp - sessionData.startTime.getTime()) / 1000),
          latitude: point.latitude,
          longitude: point.longitude,
          altitude_m: point.altitude,
          speed_mps: index > 0 ? calculateSpeedFromGPS(sessionData.gpsData[index-1], point) : 0
        })) || []
      };
      
      setMatchedActivity(activityData);
      setShowActivityExplorer(true);
      setShowSessionResults(false);
    }
  };

  const handleMarkIncomplete = () => {
    if (workout) {
      const result = DataService.markWorkoutIncomplete(workout.id);
      if (result.success) {
        // Update local state to show as active again
        setWorkout(prev => prev ? {
          ...prev,
          status: 'active',
          completedAt: undefined
        } : null);
        
        console.log('✅ Workout marked as incomplete:', result.message);
      } else {
        console.error('❌ Failed to mark workout incomplete:', result.message);
      }
    }
  };

  // 🆕 JAN 22, 2026: Header checkmark toggle handler
  const handleToggleComplete = () => {
    if (!workout) return;
    
    if (workout.state === 'completed' || workout.status === 'completed') {
      // Mark incomplete
      handleMarkIncomplete();
    } else {
      // Mark complete - open manual completion modal
      setShowManualCompletionModal(true);
    }
  };

  // 🔧 FEB 1, 2026: Simplified completion handler (no unchecking from detail view)
  // Detail view is focused on ONE workout - only complete, never uncheck
  // Unchecking should only happen from list view (accidental clicks)
  const handleWorkoutComplete = () => {
    if (!workout) return;

    // Check if already completed
    if (workout.status === 'completed') {
      console.log('⚠️ Workout already completed, no action taken');
      return;
    }
    
    // Open modal for completion
    console.log('📋 Opening manual completion modal for:', workout.id);
    setShowManualCompletionModal(true);
  };

  // Sprint 3 Task 3.1: Handle manual completion confirmation
  const handleManualCompletionConfirm = async (notes?: string) => {
    if (!workout) return;

    try {
      const result = await DataService.markWorkoutComplete(workout.id, notes);
      if (result.success) {
        console.log('✅ Workout completed manually:', result.message);
        if (result.activity_id) {
          console.log('📊 Activity created:', result.activity_id);
          console.log('💬 Post-workout debrief will arrive in chat within ~15 seconds');
        }
        if (notes) {
          console.log('Completion notes:', notes);
        }
        
        // Update local state to show completion
        setWorkout(prev => prev ? {
          ...prev,
          status: 'completed',
          completedAt: new Date(),
          activity_id: result.activity_id
        } : null);

        // If activity_id not yet available, start polling for it
        if (!result.activity_id) {
          console.log('[WorkoutDetail] Manual completion but no activity_id yet — starting poll');
          startActivityIdPolling();
        }

        // Close modal
        setShowManualCompletionModal(false);
      } else {
        console.error('❌ Failed to complete workout:', result.message);
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('❌ Manual completion error:', error);
      throw error;
    }
  };

  const calculateSpeedFromGPS = (prevPoint: any, currPoint: any) => {
    if (!prevPoint || !currPoint) return 0;
    const timeDiff = (currPoint.timestamp - prevPoint.timestamp) / 1000; // seconds
    const distance = calculateDistance(
      prevPoint.latitude, prevPoint.longitude,
      currPoint.latitude, currPoint.longitude
    );
    return timeDiff > 0 ? distance / timeDiff : 0;
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // 🔧 REACT ERROR #31 FIX (Jan 22, 2026): Helper to sanitize Date objects AND empty objects
  // Date objects AND empty objects {} CANNOT be rendered in JSX
  const sanitizeDateObjects = (obj: any): any => {
    if (!obj || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return obj.toISOString();
    if (Array.isArray(obj)) return obj.map(sanitizeDateObjects);
    
    // 🚨 CRITICAL FIX: Detect empty objects {} and convert to undefined
    if (Object.keys(obj).length === 0) {
      return undefined;
    }
    
    const sanitized: any = {};
    for (const key in obj) {
      const value = obj[key];
      if (value instanceof Date) {
        sanitized[key] = value.toISOString();
      } else if (value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
        // 🚨 CRITICAL FIX: Empty object {} → undefined (prevents React Error #31)
        sanitized[key] = undefined;
      } else if (Array.isArray(value)) {
        sanitized[key] = value.map(sanitizeDateObjects);
      } else if (value && typeof value === 'object') {
        sanitized[key] = sanitizeDateObjects(value);
      } else {
        sanitized[key] = value;
      }
    }
    return sanitized;
  };

  // Load workout data
  useEffect(() => {
    const loadWorkout = () => {
      const workoutData = DataService.getWorkoutById(workoutId);
      
      console.log('📋 WorkoutDetail: Loaded workout', workoutId);
      
      // 🔧 REACT ERROR #31 FIX: Sanitize Date objects BEFORE setting state
      const sanitizedWorkout = workoutData ? sanitizeDateObjects(workoutData) : null;
      
      setWorkout(sanitizedWorkout);
      
      // 🆕 PHASE 2: Load component mobility (warmups/cooldowns) for this workout
      const mobilityForWorkout = DataService.getComponentMobilityForWorkout(workoutId);
      
      // 🔧 REACT ERROR #31 FIX: Sanitize mobility data too
      const sanitizedMobility = mobilityForWorkout.map(sanitizeDateObjects);
      
      setComponentMobility(sanitizedMobility);
      console.log('📋 Component mobility loaded:', sanitizedMobility.length, 'routines');
      
      // 🆕 PHASE 2.3: Categorize component mobility as warmup vs cooldown
      if (mobilityForWorkout.length > 0) {
        mobilityForWorkout.forEach((mobility: any) => {
          const title = typeof mobility.title === 'string' ? mobility.title.toLowerCase() : '';
          const description = typeof mobility.description === 'string' ? mobility.description.toLowerCase() : '';
          
          // Warmup and cooldown detection (silent)
          // Logic preserved for future use
        });
      }
      
      if (workoutData) {
        // 🎯 BUG FIX JAN 5, 2026: Extract exercises from phases OR flat array
        let extractedExercises: any[] = [];
        
        // First, try to extract from phases (preferred for structured workouts)
        const phases = workoutData.phases || [];
        if (phases.length > 0) {
          phases.forEach((phase: any) => {
            // 🔧 FIX: Check BOTH exercises and exercises_json fields
            const phaseExercises = phase.exercises || phase.exercises_json || [];
            if (Array.isArray(phaseExercises) && phaseExercises.length > 0) {
              extractedExercises.push(...phaseExercises);
            }
          });
        }
        
        // If no exercises in phases, try flat exercises array
        if (extractedExercises.length === 0 && workoutData.exercises && Array.isArray(workoutData.exercises)) {
          extractedExercises = workoutData.exercises;
        }
        
        // If we have exercises, map them to BaseExercise format
        if (extractedExercises.length > 0) {
          const exerciseList: BaseExercise[] = extractedExercises.map((ex: any, index: number) => {
            // 🔧 FIX: Handle both exercise object formats
            // exercises_json has: { exercise, reps, sets, rest_seconds }
            // exercises has: { name, reps, sets, rest_seconds, form_cues, ... }
            const exerciseName = ex.name || ex.exercise || 'Exercise';
            
            return {
              id: ex.id || `exercise-${index}`,
                name: exerciseName,
              description: ex.instructions || ex.description || '',
              type: 'strength' as const,
              duration: ex.duration_minutes || (ex.reps && typeof ex.reps === 'string' && ex.reps.includes('minutes') ? parseInt(ex.reps) : undefined),
              repetitions: ex.reps && typeof ex.reps === 'string' ? parseInt(ex.reps) : undefined,
              sets: ex.sets || 1,
              restPeriod: ex.rest_seconds || ex.rest ? parseInt(String(ex.rest_seconds || ex.rest)) : undefined,
              instructions: ex.form_cues || (ex.instructions ? [ex.instructions] : []),
              precautions: ex.safety_notes || [],
              images: ex.media_urls || ex.images || [],
              targetMuscles: ex.target_muscles || [], // 🆕 JAN 29, 2026: Map target muscles for subhead display
              equipment: ex.equipment || [], // 🆕 JAN 29, 2026: Map per-exercise equipment
              weight: ex.reps || undefined // 🆕 JAN 29, 2026: Store raw reps string for duration detection (e.g., "30-40 s/side")
            };
          });
          setExercises(exerciseList);
        } else {
          // Fallback to mock exercises only if no real exercises exist
          const enhancedExercises = generateEnhancedExerciseSteps(workoutData);
          const exerciseList: BaseExercise[] = enhancedExercises.map(step => ({
            id: step.id,
            name: step.name,
            description: step.description,
            type: 'strength' as const,
            duration: step.duration ? parseInt(step.duration) : undefined,
            repetitions: step.reps ? parseInt(step.reps.split('-')[0]) : undefined,
            sets: step.sets ? parseInt(step.sets) : undefined,
            restPeriod: step.rest ? parseInt(step.rest) : undefined,
            instructions: step.form_cues,
            precautions: step.safety_notes,
            images: step.images
          }));
          setExercises(exerciseList);
        }
      }
      setLoading(false);
    };

    loadWorkout();
  }, [workoutId]);

  // 🆕 PHASE 3: Load user's HR zones
  useEffect(() => {
    const loadHrZones = async () => {
      try {
        const userId = getCurrentUserId();
        if (!userId) {
          console.log('❌ No userId found, cannot load HR zones');
          return;
        }
        
        const hrZones = await DataService.getUserHeartRateZones(userId);
        if (hrZones) {
          setUserHrZones(hrZones);
          console.log('✅ Loaded user HR zones:', hrZones.source);
        } else {
          console.log('ℹ️ No HR zones found for user');
        }
      } catch (error) {
        console.error('❌ Error loading HR zones:', error);
      }
    };

    loadHrZones();
  }, []);

  // 🆕 POST-COMPLETION: Load activity data for completed workouts
  // Retry polling handles the pipeline delay (Garmin ~5-15s, Apple Health ~3-10s, timer ~immediate)
  useEffect(() => {
    let cancelled = false;

    const loadActivityData = async () => {
      if (!workout || workout.status !== 'completed') return;

      setLoadingActivityData(true);

      const jwt_token = await storage.getItem('jwt_token');
      if (!jwt_token) {
        console.log('❌ No JWT token found');
        setLoadingActivityData(false);
        return;
      }

      // P3-14: Extended backoff covering 100s total for Garmin (60-90s arrival).
      // [0, 3s×4, 5s×4, 10s×6] = 100s total, 15 attempts
      const delays = [0, 3000, 3000, 3000, 3000, 5000, 5000, 5000, 5000, 10000, 10000, 10000, 10000, 10000, 10000];
      let foundData = false;

      setDataArrivingPhase('polling');

      for (let attempt = 0; attempt < delays.length; attempt++) {
        if (cancelled) return;
        if (attempt > 0) {
          await new Promise(resolve => setTimeout(resolve, delays[attempt]));
        }
        if (cancelled) return;

        // P3-15: After ~30s, transition to patient "waiting" phase
        if (attempt >= 8) {
          setDataArrivingPhase('waiting');
        }

        try {
          console.log(`📊 POST-COMPLETION: Attempt ${attempt + 1}/${delays.length} for workout ${workout.id}`);

          const response = await fetch(
            `${API_BASE_URL}/api/workouts/${workout.id}/activity-data`,
            {
              headers: {
                'Authorization': `Bearer ${jwt_token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (!response.ok) {
            console.log(`⚠️ Attempt ${attempt + 1}: No activity data (status: ${response.status})`);
            continue;
          }

          const responseData = await response.json();
          const data = responseData.activity;

          if (!data) {
            console.log(`⚠️ Attempt ${attempt + 1}: activity null, pipeline may still be processing`);
            continue;
          }

          console.log('✅ POST-COMPLETION: Activity data fetched', {
            activityId: data.id,
            source: data.source_type,
            sport: data.sport,
            duration: data.duration_seconds,
            distance: data.distance_meters,
            hasHeartRate: !!data.average_heart_rate,
            hasTrackpoints: !!data.trackpoints,
            hasLapData: !!data.lap_data,
            hasSamples: !!data.samples
          });

          if (cancelled) return;
          setActivityData(data);

          // Convert to UnifiedSessionData using SessionDataAdapter
          let unifiedData = null;
          if (data.source_type === 'garmin' || data.source_type === 'garmin_connect') {
            unifiedData = SessionDataAdapter.fromGarmin(data);
          } else if (data.source_type === 'strava' || data.source_type === 'strava_connect') {
            unifiedData = SessionDataAdapter.fromStrava(data);
          } else if (data.source_type === 'cedar' || data.source_type === 'in_app' || data.source_type === 'cedar_manual') {
            unifiedData = SessionDataAdapter.fromCedar(data);
          } else if (data.source_type === 'apple_health' || data.source_type === 'ios' || data.source_type === 'healthkit') {
            unifiedData = SessionDataAdapter.fromAppleHealth(data);
          } else if (data.source_type === 'android_health' || data.source_type === 'google_fit' || data.source_type === 'health_connect') {
            unifiedData = SessionDataAdapter.fromAndroidHealth(data);
          } else if (data.source_type === 'google_health') {
            // Google Health uses the same Garmin-shaped unified_athletic_data
            // schema — read with fromGarmin, stamp the honest source. Without
            // this branch unifiedData stayed null → no rich post-completion view
            // despite full backend ingestion. Parity hardening #25 (legacy web).
            unifiedData = { ...SessionDataAdapter.fromGarmin(data), source: 'google_health' };
          } else if (data.source_type === 'manual_entry') {
            unifiedData = SessionDataAdapter.fromCedar(data);
          }

          if (unifiedData) {
            setUnifiedSession(unifiedData);
            console.log('✅ POST-COMPLETION: Converted to UnifiedSessionData', {
              sessionType: unifiedData.sessionType,
              source: unifiedData.source,
            });
          }

          foundData = true;
          setDataArrivingPhase('idle');
          break; // Success — stop retrying
        } catch (error) {
          console.error(`❌ Attempt ${attempt + 1} error:`, error);
        }
      }

      // P3-15: Mark as unavailable if polling exhausted
      if (!foundData && !cancelled) {
        setDataArrivingPhase('unavailable');
      }

      // Graceful degradation: build minimal session from workout's prescribed exercises
      if (!foundData && !cancelled && workout.exercises?.length > 0) {
        console.log('📋 POST-COMPLETION: No activity data — using prescribed exercises as fallback');
        const exercises = (workout.exercises as any[]).map((ex: any) => ({
          exerciseName: ex.name || ex.exerciseName || 'Exercise',
          prescribedSets: ex.sets || ex.prescribedSets || 0,
          prescribedReps: String(ex.reps || ex.prescribedReps || '0'),
          prescribedWeight: ex.weight || ex.prescribedWeight || 0,
          actualSets: ex.sets || ex.prescribedSets || 0,
          actualReps: Array(ex.sets || ex.prescribedSets || 0).fill(parseInt(String(ex.reps || ex.prescribedReps || '0'))),
          actualWeight: Array(ex.sets || ex.prescribedSets || 0).fill(ex.weight || ex.prescribedWeight || 0),
          completed: true,
        }));
        setUnifiedSession({
          id: workout.id,
          sessionType: 'strength',
          source: 'manual',
          completedAt: new Date(),
          totalDuration: (workout.duration || 0) * 60,
          exercises,
        } as any);
      }

      if (!cancelled) setLoadingActivityData(false);
    };

    loadActivityData();
    return () => { cancelled = true; };
  }, [workout?.status, workout?.id]);
  
  // Debug logging
  console.log('🔍 WorkoutDetail Debug:', {
    workoutId,
    workout: workout?.title,
    status: workout?.status,
    type: workout?.type,
    activityType: workout?.activityType,
    hasPhases: !!workout?.phases?.length,
    hasExercises: !!workout?.exercises?.length,
    hasWorkoutData: !!workout?.workout_data,
    associatedMobilityCount: workout?.associatedMobility?.length || 0
  });

  useEffect(() => {
    const loadWorkoutBriefing = async () => {
      if (!workout) {
        return;
      }

      // Simulate API call - in real app this would be LLM-generated content
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Enhanced mock data with comprehensive exercise information
      setWorkoutBriefing({
        workout_id: workout.id,
        introduction: `This ${String(workoutType || 'workout')} workout is designed to ${getWorkoutObjective(String(workoutType || 'cardio'))}. Perfect for intermediate level athletes.`,
        equipment_needed: ['bodyweight'],
        warm_up_guidance: "Begin with 5-10 minutes of light cardio and dynamic stretching to prepare your body for the workout ahead.",
        cool_down_guidance: "Finish with 5-10 minutes of static stretching and deep breathing to aid recovery.",
        exercise_steps: generateEnhancedExerciseSteps(workout),
        progression_notes: "Increase intensity by adding weight, reps, or duration as you get stronger. Listen to your body and progress gradually.",
        modification_options: ["Beginner: Reduce reps/duration by 25%", "Advanced: Add weight or increase complexity", "Injury modification: Substitute with low-impact alternatives"],
        estimated_duration: typeof workout.duration === 'number' ? workout.duration : (parseInt(String(workout.duration || '45').split(' ')[0]) || 45),
        difficulty_level: 'Moderate',
        target_muscles: getTargetMuscles(String(workoutType || 'cardio'))
      });
    };

    if (workout) {
      loadWorkoutBriefing();
    }
  }, [workout]); // Changed dependency to workout instead of workoutId

  // Early return checks - AFTER all hooks
  if (!workout && !loading) {
    console.error('🚨 WorkoutDetail Error: Workout not found', { workoutId });
    return (
      <div className="flex flex-col h-full bg-white">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <button 
            onClick={handleSmartBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-lg font-semibold">Workout Not Found</h1>
          <div className="w-6 h-6" />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Workout Not Found</h2>
            <p className="text-gray-600 mb-4">The workout with ID "{workoutId}" could not be found.</p>
            <button 
              onClick={handleSmartBack}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading || !workout) {
    return (
      <div className="flex flex-col h-full bg-white">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <button 
            onClick={handleSmartBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-lg font-semibold">Loading...</h1>
          <div className="w-6 h-6" />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading workout details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Get workout categorization info (after early return when workout is guaranteed to exist)
  // 🔧 CRITICAL FIX: Use workoutType (defined above) instead of workout.type to prevent object rendering
  const workoutTypeConfig = WorkoutCategorizationService.getWorkoutTypeConfig(String(workoutType || 'cardio'));

  const handleExerciseToggle = (exerciseId: string) => {
    setExpandedExercise(expandedExercise === exerciseId ? null : exerciseId);
  };

  const getTypeIcon = () => {
    // 🔧 CRITICAL FIX: Use workoutType variable instead of workout.type
    switch (workoutType) {
      case 'cardio':
        return (
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'strength':
        return (
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        );
      case 'flexibility':
        return (
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        );
      case 'hiit':
        return (
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        );
      default:
        return (
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
    }
  };

  const getTypeColor = () => {
    if (workoutTypeConfig) {
      return workoutTypeConfig.color.replace('bg-', 'text-').replace('-100', '-600');
    }
    
    // 🔧 CRITICAL FIX: Use workoutType variable instead of workout.type
    switch (workoutType) {
      case 'cardio':
        return 'text-red-600';
      case 'strength':
        return 'text-purple-600';
      case 'flexibility':
        return 'text-pink-600';
      case 'hiit':
        return 'text-orange-600';
      case 'recovery':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatWatchStatus = (status?: string) => {
    switch (status) {
      case 'pushed':
        return { label: 'On your watch', color: 'bg-green-100 text-green-800' };
      case 'pending':
        return { label: 'Pending push', color: 'bg-amber-100 text-amber-800' };
      case 'failed':
        return { label: 'Push failed', color: 'bg-red-100 text-red-800' };
      default:
        return { label: 'Not pushed', color: 'bg-gray-100 text-gray-700' };
    }
  };

  const formatWatchPlatform = (platform?: string | null) => {
    if (platform === 'garmin') return 'Garmin';
    if (platform === 'apple_watch') return 'Apple Watch';
    return 'Watch';
  };

  const formatPushedAt = (date?: Date | string | null) => {
    if (!date) return null;
    try {
      const d = typeof date === 'string' ? new Date(date) : date;
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }).format(d);
    } catch {
      return null;
    }
  };

  // Format dates for display
  const formatDetailDate = (date: Date | string | undefined) => {
    if (!date) return null;
    // 🎯 BUG FIX: Remove time from date display - users can do workouts anytime during the day
    // 🚨 CRITICAL FIX (Jan 4, 2026): Prevent timezone conversion causing off-by-one day errors
    // Database stores: "2026-01-05" (Monday, January 5)
    // Without fix: Shows "Sunday, January 4, 2026" (WRONG - off by 1 day)
    // With fix: Shows "Monday, January 5, 2026" (CORRECT - matches database)
    
    // 🔧 REACT ERROR #31 FIX (Jan 21, 2026): Handle ISO strings from sanitization
    // After sanitization, date might be: '2026-01-22T00:00:00.000Z' (full ISO)
    // Need to extract just the date part: '2026-01-22'
    let dateStr: string;
    if (typeof date === 'string') {
      // Extract date part (YYYY-MM-DD) from full ISO string or use as-is
      dateStr = date.includes('T') ? date.split('T')[0] : date;
    } else {
      // Date object (shouldn't happen after sanitization, but handle gracefully)
      dateStr = date.toISOString().split('T')[0];
    }
    
    // Create date at noon UTC to avoid timezone shifts
    const dateAtNoon = new Date(dateStr + 'T12:00:00Z');
    
    // Validate date before formatting
    if (isNaN(dateAtNoon.getTime())) {
      console.error('❌ Invalid date created:', dateStr, 'from original:', date);
      return 'Invalid date';
    }
    
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC' // Force UTC to match database dates exactly
    }).format(dateAtNoon);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cedar-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading workout...</p>
        </div>
      </div>
    );
  }

  // Workout not found
  if (!workout) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Workout not found</p>
          <button
            onClick={handleSmartBack}
            className="px-4 py-2 bg-cedar-primary text-white rounded-lg hover:bg-cedar-dark transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // 🏠 HOME BUTTON PATTERN (Jan 22, 2026): Handle bottom navigation
  // When Plan tab clicked from workout detail, always navigate to Today view
  const handleNavigation = (tab: string) => {
    switch (tab) {
      case 'cedar':
        router.push('/chat');
        break;
      case 'my-path':
      case 'mypath':
        router.push('/mypath?resetToToday=true');
        break;
      case 'momentum':
      case 'insights':
        router.push('/insights');
        break;
      case 'settings':
        router.push('/settings');
        break;
      default:
        router.push('/chat');
        break;
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50 max-w-md mx-auto flex flex-col" style={{ position: 'relative' }}>
      {/* 🔧 JAN 22, 2026: Simplified Header - Back button only */}
      <div className="bg-white border-b border-gray-200 px-4 sticky top-0 z-50 bg-white flex-shrink-0"
           style={{ 
             paddingTop: 'max(12px, env(safe-area-inset-top))',
             paddingBottom: '12px'
           }}>
        {/* Top row: Back button + Right actions (checkmark + chat) */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={handleSmartBack}
            className="flex items-center text-gray-600 hover:text-gray-900 p-1 -ml-1 flex-shrink-0"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Back</span>
          </button>
          
          {/* 🔧 JAN 22, 2026 SPRINT 3: Right actions - Match SimpleWorkoutCard styling exactly */}
          <div className="flex items-center gap-3">
            {/* 🔧 FEB 1, 2026: Checkmark - Clickable for manual completion (opens modal) */}
            {/* DETAIL VIEW: Clickable (focused on one workout, intentional action) */}
            {/* LIST VIEW: Non-clickable (quick scan, accidental clicks) - See SimpleWorkoutCard.tsx */}
            {/* 🔧 BUG #15 FIX (Jan 31, 2026): Check workout.status (not workout.state) */}
            {workout.status === 'completed' ? (
              <div 
                className="flex-shrink-0"
                title="Completed"
              >
                <div className="w-5 h-5 bg-[var(--cedar-primary)] rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            ) : (
              <button
                onClick={handleWorkoutComplete}
                className="flex-shrink-0 w-5 h-5 border-2 border-gray-300 rounded-full flex items-center justify-center hover:border-gray-400 hover:bg-gray-50 transition-colors cursor-pointer"
                title="Click to mark complete"
              >
                {/* 🔧 FEB 1, 2026: Grey checkmark - clickable for manual completion */}
                <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
            )}
            
            {/* Chat button - size="small" matches list view */}
            <ContextualChatButton
              context={{
                type: 'workout',
                id: workout.id,
                title: workout.title || 'Workout',
                description: workout.description || workout.workout_data?.description
              }}
              variant="clean"
              size="small"
            />
          </div>
        </div>
        
        {/* Title row - with activity icon and device badge (Sprint 1 Task 1.4, Sprint 3 Task 3.2) */}
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-3">
            {/* 🆕 JAN 2026: Device attribution for completed workouts */}
            {(() => {
              // 🔧 REACT ERROR #31 FIX: Safeguard data sources before rendering badges
              if (workout.status !== 'completed' || !workout.activity_id) return null;
              
              try {
                const sources = getDataSources(workout);
                
                // Verify sources is a valid array
                if (!Array.isArray(sources) || sources.length === 0) {
                  console.log('⚠️ REACT ERROR #31 PREVENTION: Invalid sources array:', {
                    sources,
                    type: typeof sources,
                    isArray: Array.isArray(sources)
                  });
                  return null;
                }
                
                // Verify each source is a valid string
                const invalidSource = sources.find(s => typeof s !== 'string');
                if (invalidSource !== undefined) {
                  console.error('⚠️ REACT ERROR #31 PREVENTION: Invalid source in array:', {
                    invalidSource,
                    type: typeof invalidSource,
                    isEmptyObject: typeof invalidSource === 'object' && Object.keys(invalidSource || {}).length === 0
                  });
                  return null;
                }
                
                return (
                  <div className="flex items-center gap-2 mb-2">
                    <DataSourceBadges sources={sources} />
                  </div>
                );
              } catch (error) {
                console.error('❌ REACT ERROR #31 PREVENTION: Error in getDataSources:', error);
                return null;
              }
            })()}
            <WorkoutIcon 
              iconType={getWorkoutIconType(
                typeof workout.type === 'string' ? workout.type : (typeof workout.workout_type === 'string' ? workout.workout_type : 'cardio'),
                typeof workout.activityType === 'string' ? workout.activityType : (typeof workout.activity_type === 'string' ? workout.activity_type : undefined)
              )}
            />
            {/* 🆕 STRENGTH-AUDIT-8: Adaptive font sizing for workout title */}
            <h1 
              className="font-bold text-gray-900"
              style={{
                fontSize: (() => {
                  const title = String(workout.title || 'Workout');
                  const displayTitle = title.split(':')[0].trim();
                  if (displayTitle.length > 35) return '14px';
                  if (displayTitle.length > 25) return '16px';
                  return '18px';
                })(),
                lineHeight: '1.3'
              }}
            >
              {String(workout.title || 'Workout').split(':')[0].trim()}
            </h1>
          </div>
          
          {/* Device Push Badge - Sprint 3 Task 3.2 */}
          {(() => {
            // 🔧 REACT ERROR #31 FIX: Safeguard device badge props
            if (!shouldShowPushStatus(workout)) return null;
            
            try {
              // Type-safe platform detection
              const watchPlatform = workout.watchPlatform;
              const isValidPlatform = typeof watchPlatform === 'string' && 
                                      (watchPlatform === 'garmin' || watchPlatform === 'apple_watch');
              
              const platform: 'garmin' | 'apple_watch' = isValidPlatform ? 
                watchPlatform as 'garmin' | 'apple_watch' : 'garmin';
              
              // Type-safe device model
              const deviceModel = typeof workout.deviceModel === 'string' ? workout.deviceModel : undefined;
              
              return (
                <PushedToDeviceBadge 
                  platform={platform}
                  deviceModel={deviceModel}
                  size="small"
                />
              );
            } catch (error) {
              console.error('❌ REACT ERROR #31 PREVENTION: Error in PushedToDeviceBadge:', error);
              return null;
            }
          })()}
        </div>
        
        {/* Data Source Badges (Post-Completion) - Sprint 1 Task 2 */}
        {(() => {
          // 🔧 REACT ERROR #31 FIX: Safeguard data sources before rendering badges (duplicate of header check)
          if (!shouldShowDataSources(workout)) return null;
          
          try {
            const sources = getDataSources(workout);
            
            // Verify sources is a valid array with string elements
            if (!Array.isArray(sources) || sources.length === 0) {
              return null;
            }
            
            const invalidSource = sources.find(s => typeof s !== 'string');
            if (invalidSource !== undefined) {
              console.error('⚠️ REACT ERROR #31 PREVENTION: Invalid source (header badges):', {
                invalidSource,
                type: typeof invalidSource
              });
              return null;
            }
            
            return (
              <div className="mb-2">
                <DataSourceBadges 
                  sources={sources}
                  size="small"
                />
              </div>
            );
          } catch (error) {
            console.error('❌ REACT ERROR #31 PREVENTION: Error in header badges:', error);
            return null;
          }
        })()}
        
        {/* Date badge (Task 11: single "Today" pill when scheduled today, otherwise full date) */}
        {(workout.scheduledDate || workout.completedAt) && (() => {
          const rawDate = workout.completedAt || workout.scheduledDate!;
          // Use local date parts to avoid UTC mismatch at night in negative-offset timezones
          const now = new Date();
          const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

          let workoutDateStr = '';
          if (typeof rawDate === 'string' && rawDate.match(/^\d{4}-\d{2}-\d{2}/)) {
            workoutDateStr = rawDate.substring(0, 10);
          } else if (rawDate) {
            const d = new Date(rawDate);
            workoutDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          }
          const isToday = workoutDateStr === todayStr;

          if (isToday) {
            return (
              <div className="mb-2">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                  Today
                </span>
              </div>
            );
          }
          return (
            <div className="text-sm text-gray-500 mb-2">
              {formatDetailDate(rawDate)}
            </div>
          );
        })()}
        
        {/* Professional type badge and metadata */}
        <div className="flex items-center gap-2 mb-2">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${workoutTypeConfig?.color || 'bg-gray-100 text-gray-800'}`}>
            {workoutTypeConfig?.label || String(workoutType || 'Workout')}
          </span>
          
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {String(workout.duration || workout.duration_minutes || 0)} min
          </span>
        </div>
        
        {/* 🆕 FEB 1, 2026: ROW 5 - Metric Pills (Cardio Pre-Completion Only) */}
        {(() => {
          const isCardio = (workout.type || workout.workout_type || workout.activityType || workout.activity_type) === 'cardio';
          const isPre = workout.status !== 'completed' && workout.state !== 'completed';
          
          if (!isCardio || !isPre) return null;
          
          const distance = workout.estimated_distance || workout.distance_km;
          const calories = workout.workout_data?.estimated_calories;
          
          // Get HR zone from workout_data or phases
          const hrZone = workout.workout_data?.hr_zone || 
                        (() => {
                          const phases = workout.workout_data?.phases || workout.phases || [];
                          if (Array.isArray(phases) && phases.length > 0) {
                            const zones = phases
                              .map((p: any) => p.hr_zone)
                              .filter((z: number) => z && z > 0);
                            return zones.length > 0 ? zones[0] : null;
                          }
                          return null;
                        })();
          
          const hasAnyMetrics = (distance && distance > 0) || (calories && calories > 0) || (hrZone && userHrZones);
          
          if (!hasAnyMetrics) return null;
          
          // Get zone name for display
          const getZoneName = (zone: number): string => {
            switch (zone) {
              case 1: return 'Recovery';
              case 2: return 'Easy';
              case 3: return 'Tempo';
              case 4: return 'Threshold';
              case 5: return 'Max';
              default: return 'Z' + zone;
            }
          };
          
          return (
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {/* Distance pill */}
              {distance && distance > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  📏 {distance.toFixed(1)} Km
                </span>
              )}
              
              {/* Calories pill */}
              {calories && calories > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                  🔥 {calories} kcal
                </span>
              )}
              
              {/* HR Zone pill */}
              {hrZone && userHrZones && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                  ❤️ Zone {hrZone}: {getZoneName(hrZone)}
                </span>
              )}
            </div>
          );
        })()}
      </div>

      {/* 🆕 JAN 22, 2026: Scrollable content area with padding for fixed action button + nav */}
      <div 
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={{ 
          display: showUnifiedTimer ? 'none' : undefined, // 🔧 FEB 13: Hide scrollable content when timer is fullscreen overlay
          paddingBottom: 'calc(152px + env(safe-area-inset-bottom, 0px))', // 70px action button + 82px nav
          WebkitOverflowScrolling: 'touch'
        }}
      >

      {/* 🆕 JAN 31, 2026: REORDERED - Coaching Notes FIRST (Today's Session + Why) */}
      {(() => {
        const notesRaw = workout.workout_data?.coaching_notes || 
                        workout.coaching_notes || 
                        workout.workout_data?.daily_notes ||
                        workout.description;
        
        // 🚨 FIX (Jan 29, 2026): Pass object directly to ExpandableCoachingNotes
        // Component handles both object {what, why} and legacy string formats
        return notesRaw ? (
          <div className="mx-4 mt-4 mb-4">
            <ExpandableCoachingNotes notes={notesRaw} />
          </div>
        ) : null;
      })()}

      {/* 🆕 JAN 31, 2026: REORDERED - Weather SECOND (after coaching notes) */}
      {/* Weather Forecast - Mission 14 Integration */}
      {/* 🔧 ISSUE 2 FIX: Only show weather for outdoor workouts */}
      {(() => {
        // 🔧 REACT ERROR #31 FIX: Safeguard scheduledDate before passing to child component
        const scheduledDate = workout.scheduledDate;
        const isValidDate = scheduledDate && (scheduledDate instanceof Date || typeof scheduledDate === 'string');
        
        if (!isValidDate || !isOutdoorWorkout(workout)) {
          return null;
        }
        
        return (
          <div className="mx-4 mb-4">
            <WorkoutWeatherDetail 
              workoutDate={scheduledDate}
              guidance={typeof workout.workout_data?.weather_guidance === 'string' ? workout.workout_data.weather_guidance : undefined}
            />
          </div>
        );
      })()}

      {/* Task 13: Complete Session Flow section removed (duplicated Session Plan below) */}

      {/* 🆕 JAN 31, 2026: REMOVED - Metrics Grid + Equipment moved to right before main content */}
      {/* See lines ~2750+ for new placement */}

      {/* 📊 Target vs Actual (Nov 2025) */}
      {workout.metadata?.target_metrics && workout.metadata?.actual_metrics && (
        <div className="mx-4 mt-4 p-3 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold mb-2">Target vs Actual</h4>
          <div className="space-y-1 text-sm">
            {workout.metadata?.target_metrics && typeof workout.metadata.target_metrics === 'object' && 
             Object.entries(workout.metadata.target_metrics).map(([key, target]) => {
              const actual = workout.metadata?.actual_metrics?.[key];
              if (!actual) return null;
              
              return (
                <div key={key} className="flex justify-between">
                  <span className="text-gray-600 capitalize">
                    {key.replace(/_/g, ' ')}:
                  </span>
                  <span>
                    <span className="font-medium">{String(actual)}</span>
                    <span className="text-gray-400 mx-2">/</span>
                    <span className="text-gray-500">{String(target)}</span>
                  </span>
                </div>
              );
            })}
          </div>
          {workout.metadata.assessment && (
            <p className="mt-2 text-xs text-gray-600 italic">{String(workout.metadata.assessment)}</p>
          )}
        </div>
      )}


      {/* Content - Natural scroll under fixed header */}
      <div className="p-4 pb-64">
        {/* 🏋️ Post-Workout Insights (Mission 22) - Show at top if completed */}
        {(() => {
          // 🔧 REACT ERROR #31 FIX: Safeguard activity_id before passing to HowItWentSection
          if (workout.status !== 'completed') return null;
          
          const activityId = workout.activity_id;
          
          // Type check: Must be a string, not an empty object or other type
          if (typeof activityId !== 'string' || !activityId) {
            console.log('⚠️ REACT ERROR #31 PREVENTION: activity_id is not a valid string:', {
              activityId,
              type: typeof activityId,
              isObject: typeof activityId === 'object',
              isEmptyObject: typeof activityId === 'object' && activityId !== null && Object.keys(activityId).length === 0
            });
            return null;
          }
          
          return (
            <div className="mb-6">
              <HowItWentSection activityId={activityId} />
            </div>
          );
        })()}

        {/* 🆕 POST-COMPLETION EXPANDABLE SECTIONS - Progressive Disclosure */}
        {/* P3-14/15: Extended polling with "data arriving" friendly states */}
        {workout.status === 'completed' && (
          loadingActivityData && dataArrivingPhase === 'waiting' ? (
            <div className="mx-4 mb-6 p-5 bg-[#1F6C4F]/5 border border-[#1F6C4F]/15 rounded-xl text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#1F6C4F]/30 border-t-[#1F6C4F] mx-auto mb-3" />
              <div className="text-sm font-semibold text-gray-800">Syncing session data...</div>
              <div className="text-xs text-gray-500 mt-1">
                {(workout as any).watch_platform === 'garmin'
                  ? 'Waiting for Garmin to finish syncing'
                  : 'This usually takes under a minute'}
              </div>
            </div>
          ) : loadingActivityData ? (
            <div className="space-y-4 mb-6 px-4">
              <div className="animate-pulse space-y-3">
                <div className="h-12 bg-gray-200 rounded-lg" />
                <div className="h-12 bg-gray-200 rounded-lg" />
                <div className="h-12 bg-gray-200 rounded-lg" />
              </div>
            </div>
          ) : unifiedSession ? (
          <div className="space-y-4 mb-6">
            {/* 🗺️ GPS Route Map - for outdoor activities with GPS tracking */}
            {hasGPSData(unifiedSession) && (
              <ExpandableGPSRouteMap
                session={unifiedSession}
                isInitiallyExpanded={false}
              />
            )}

            {/* Phase Breakdown - for structured workouts */}
            {hasPhases(unifiedSession) && (
              <ExpandablePhaseBreakdown phases={unifiedSession.phases} />
            )}

            {/* Splits - for running/cycling */}
            {hasSplits(unifiedSession) && (
              <ExpandableSplits splits={unifiedSession.splits} />
            )}

            {/* Elevation Profile - for outdoor cardio */}
            {hasElevationData(unifiedSession) && (
              <ExpandableElevation data={{
                elevationGain: unifiedSession.elevationGain,
                elevationLoss: unifiedSession.elevationLoss,
                gpsData: unifiedSession.gpsData
              }} />
            )}

            {/* Heart Rate Zones - for cardio/interval/strength */}
            {hasHeartRateData(unifiedSession) && (
              <ExpandableHeartRateZones data={{
                avgHeartRate: unifiedSession.avgHeartRate,
                maxHeartRate: unifiedSession.maxHeartRate,
                hrZones: unifiedSession.hrZones,
                samples: unifiedSession.samples?.filter(s => s.heartRate)
              }} plannedZones={workout?.workout_data?.hr_zones}
              intensityLanguage={workout?.workout_data?.intensity_language ?? null} />
            )}

            {/* Power & Cadence - for cycling */}
            {hasPowerOrCadenceData(unifiedSession) && (
              <ExpandablePowerCadence data={{
                avgPower: unifiedSession.avgPower,
                maxPower: unifiedSession.maxPower,
                normalizedPower: unifiedSession.normalizedPower,
                powerZones: unifiedSession.powerZones,
                avgCadence: unifiedSession.avgCadence,
                maxCadence: unifiedSession.maxCadence,
                samples: unifiedSession.samples?.filter(s => s.power || s.cadence)
              }} />
            )}

            {/* Strava Segments - for Strava-synced workouts */}
            {hasSegments(unifiedSession) && (
              <ExpandableSegments segments={unifiedSession.segments} />
            )}

            {/* Strength Exercises - for strength workouts */}
            {hasExercises(unifiedSession) && (
              <ExpandableExercises exercises={unifiedSession.exercises} />
            )}

            {/* HIIT Breakdown - for circuit/EMOM/AMRAP */}
            {hasHIITData(unifiedSession) && unifiedSession.hiitType && (
              <ExpandableHIITBreakdown
                hiitType={unifiedSession.hiitType}
                circuits={unifiedSession.circuits}
                emom={unifiedSession.emom}
                amrap={unifiedSession.amrap}
              />
            )}
          </div>
          ) : dataArrivingPhase === 'unavailable' ? (
            <div className="mx-4 mb-6 p-5 bg-[#1F6C4F]/5 border border-[#1F6C4F]/15 rounded-xl text-center">
              <div className="text-base mb-1">🔄</div>
              <div className="text-sm font-semibold text-gray-800">Session data hasn't arrived yet</div>
              <div className="text-xs text-gray-500 mt-1">
                {(workout as any).watch_platform === 'garmin'
                  ? 'Garmin data can take a couple of minutes to sync. Check back shortly.'
                  : 'Your device data may still be syncing. Check back in a minute.'}
              </div>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 px-4 py-1.5 bg-[#1F6C4F] text-white text-sm font-medium rounded-lg hover:bg-[#175A3F] transition-colors"
              >
                Refresh
              </button>
            </div>
          ) : (
            <div className="mx-4 mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
              <div className="text-green-700 font-medium text-sm">Workout completed</div>
              <div className="text-xs text-green-600 mt-1">No device data available for this session</div>
            </div>
          )
        )}

        {/* Task 12: Yellow dev-mode disclaimer removed */}

        {/* 🆕 JAN 31, 2026: REMOVED - Coaching Notes moved to top (after header) */}
        {/* 🆕 JAN 31, 2026: REMOVED - Warmup expandable integrated into session plan */}
        {/* 🆕 JAN 31, 2026: REMOVED - Equipment moved to right before main content */}

        {/* 📚 Research Note - Evidence-Based Exercise Guidance */}
        {(workout as any).research_note && (
          <div className="mb-6">
            <div className="bg-green-50 rounded-lg p-4 border border-green-100">
              <div className="flex items-center space-x-2 mb-3">
                <svg className="w-5 h-5 text-[#1F6C4F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <h4 className="text-sm font-semibold text-[#1F6C4F]">Research-Backed Guidance</h4>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{(workout as any).research_note}</p>
              
              {/* Deep link to parent plan's research tab */}
              {workout.planId && (
                <div className="mt-4 pt-4 border-t border-green-200">
                  <button
                    onClick={() => {
                      // Navigate to plan detail with research tab active
                      navigateToDetail('plan', workout.planId, 'workout-detail');
                      // Note: We'll need to add URL param support to open research tab directly
                    }}
                    className="text-sm text-green-700 hover:text-green-800 font-medium flex items-center space-x-1"
                  >
                    <span>See full week's research context</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 💪 Type-Specific Guidance Components */}
        {/* Weight Selection Guide for Strength Workouts */}
        {workout.workout_type?.includes('strength') && workout.status !== 'completed' && (
          <div className="mb-6">
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-purple-900 mb-2">Choosing the Right Weight</h4>
                  <ul className="text-sm text-purple-800 space-y-1.5">
                    <li>• Select a weight where the <strong>last 2-3 reps feel challenging</strong></li>
                    <li>• You should be able to complete all prescribed reps with <strong>good form</strong></li>
                    <li>• If unsure, start lighter and increase as you progress</li>
                    <li>• Your last used weights will be displayed during the workout for reference</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 🆕 AUDIT FIX JAN 19: Only show "Workout Structure" for TRUE interval workouts, not easy base runs */}
        {(() => {
          const layout = getWorkoutLayout(workout);
          // Only show for interval workouts (tempo, fartlek, progression - NOT base/easy runs)
          return layout === 'interval' && workout.activity_type?.includes('running') && 
                 workout.phases && workout.phases.length > 1 && workout.status !== 'completed';
        })() && (
          <div className="mb-6">
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-orange-900 mb-2">Run Plan</h4>
                  <div className="text-sm text-orange-800 space-y-2">
                    <p>{(() => {
                      // Generate descriptive narrative based on actual workout structure
                      // 🔧 JAN 21, 2026: Safeguard against phases being an object instead of array
                      if (!Array.isArray(workout.phases)) {
                        return `This workout includes structured phases. Cedar will guide you through each section.`;
                      }
                      
                      const phaseNames = workout.phases.map((p: any) => p.phase_name || p.name || p.phase_type || 'run').join(', ');
                      const fastestPace = Math.min(...workout.phases.map((p: any) => 
                        parseFloat(p.target_pace_min_per_km || p.target_pace || '0')
                      ).filter((p: number) => p > 0));
                      
                      return `This is a ${workout.phases.length}-part progressive run${
                        fastestPace > 0 ? ` peaking at ${fastestPace.toFixed(2)}/km` : ''
                      }. ${componentMobility.length > 0 ? 'Don\'t forget your mobility work before and after. ' : ''}Cedar will guide you through: ${phaseNames}.`;
                    })()}</p>
                    <ul className="space-y-1 mt-2">
                      {(() => {
                        // 🆕 JAN 5, 2026: Group repeating interval patterns for compact display
                        const formatPhaseGuidance = (phase: any) => {
                          // Format pace targets
                          const paceTarget = phase.target_pace_min_per_km && phase.target_pace_max_per_km
                            ? `${phase.target_pace_min_per_km}-${phase.target_pace_max_per_km}/km`
                            : phase.target_pace_min_per_km
                            ? `${phase.target_pace_min_per_km}/km`
                            : phase.target_pace
                            ? `${phase.target_pace}/km`
                            : null;
                          
                          // Calculate distance
                          const distance = phase.distance_meters 
                            ? `${(phase.distance_meters / 1000).toFixed(1)} km`
                            : phase.distance_km 
                            ? `${phase.distance_km.toFixed(1)} km`
                            : phase.duration_minutes
                            ? `${phase.duration_minutes} min`
                            : phase.duration_seconds
                            ? `${Math.round(phase.duration_seconds / 60)} min`
                            : null;
                          
                          // Format HR targets with zone names
                          let hrTarget = null;
                          if (phase.target_hr_min && phase.target_hr_max) {
                            const zoneName = getHrZoneName(phase.target_hr_min, phase.target_hr_max);
                            hrTarget = zoneName 
                              ? `${zoneName} (${phase.target_hr_min}-${phase.target_hr_max} bpm)`
                              : `${phase.target_hr_min}-${phase.target_hr_max} bpm`;
                          }
                          
                          // Build guidance string
                          const guidanceParts = [];
                          if (distance) guidanceParts.push(distance);
                          if (paceTarget) guidanceParts.push(`@ ${paceTarget}`);
                          if (hrTarget) guidanceParts.push(hrTarget);
                          
                          return guidanceParts.join(' • ') || 
                                 phase.instructions || 
                                 phase.description || 
                                 'Follow Cedar\'s guidance';
                        };
                        
                        const getDisplayName = (phase: any) => {
                          // 🔧 JAN 23, 2026: Check both phase_name (new schema) and name (immediate plan generator)
                          const phaseName = phase.phase_name || phase.name;
                          if (phaseName) {
                            return phaseName.replace(/\s+\d+$/, '').trim();
                          }
                          return phase.phase_type === 'warmup' ? 'Warm-up' :
                                 phase.phase_type === 'cooldown' ? 'Cooldown' :
                                 phase.phase_type === 'recovery' ? 'Recovery' :
                                 phase.phase_type === 'interval' ? 'Interval' :
                                 phase.phase_type === 'tempo' ? 'Tempo' :
                                 phase.phase_type === 'steady_state' ? 'Steady Run' :
                                 phase.phase_type || 'Run';
                        };
                        
                        return groupPhases(workout.phases).map((group, groupIdx) => {
                          if (group.type === 'single') {
                            // Render single phase (warmup, cooldown, standalone)
                            const phaseName = getDisplayName(group.phase);
                            const guidance = formatPhaseGuidance(group.phase);
                            
                            return (
                              <li key={groupIdx} className="flex items-start gap-2">
                                <span className="font-medium min-w-[100px]">{phaseName}:</span>
                                <span>{guidance}</span>
                              </li>
                            );
                          } else {
                            // Render repeated pattern with count prefix
                            const patternNames = group.pattern.map(p => getDisplayName(p));
                            const uniqueNames = [...new Set(patternNames)];
                            const patternDescription = uniqueNames.join(' + ');
                            
                            return (
                              <li key={groupIdx} className="my-3">
                                <div className="font-semibold text-orange-900 mb-2 text-base">
                                  {group.repeatCount}x ({patternDescription}):
                                </div>
                                <ul className="ml-4 space-y-1.5 border-l-2 border-orange-300 pl-3">
                                  {group.pattern.map((phase, patternIdx) => {
                                    const phaseName = getDisplayName(phase);
                                    const guidance = formatPhaseGuidance(phase);
                                    
                                    return (
                                      <li key={patternIdx} className="flex items-start gap-2 text-sm">
                                        <span className="font-medium text-orange-800">• {phaseName}:</span>
                                        <span className="text-orange-700">{guidance}</span>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </li>
                            );
                          }
                        });
                      })()}
                    </ul>
                    <p className="mt-2 text-xs">Voice guidance and visual cues will help you maintain the right effort level throughout.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Completed Workout Options - Show if completed */}
        {workout.status === 'completed' && (
          <div className="mb-6">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-green-900 mb-1">Workout Completed!</h4>
                    <p className="text-green-800 text-sm">
                      {workout.completedAt ? `Completed on ${formatDetailDate(workout.completedAt)}` : 'Great job finishing this workout!'}
                    </p>
                  </div>
                </div>
                {(() => {
                  const sessionHistory = DataService.getWorkoutSessionHistory(workout.id);
                  const hasSessionData = sessionHistory.length > 0;
                  
                  return !hasSessionData ? (
                    <button
                      onClick={handleWorkoutComplete}
                      className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors font-medium text-xs"
                      title="Mark as incomplete (no session data to lose)"
                    >
                      Uncheck
                    </button>
                  ) : null;
                })()}
              </div>
            </div>
          </div>
        )}

        {/* 🆕 JAN 31, 2026: REORDERED - Metrics Grid + Equipment RIGHT BEFORE MAIN CONTENT */}
        {/* 🔧 FEB 1, 2026: Hide PrimaryMetricsGrid for cardio pre-completion (header pills replace it) */}
        {/* Show appropriate metrics grid based on completion status */}
        {workout.status === 'completed' ? (
          loadingActivityData ? (
            <div className="mx-4 mt-4 mb-4">
              <div className="grid grid-cols-3 gap-3 animate-pulse">
                {[1,2,3].map(i => (
                  <div key={i} className="bg-gray-200 rounded-lg h-20" />
                ))}
              </div>
            </div>
          ) : unifiedSession ? (
          <PostCompletionMetricsGrid
            session={{
              ...unifiedSession,
              durationMinutes: unifiedSession.totalDuration / 60
            }}
            layout={getWorkoutLayout(workout) as any}
          />
          ) : (
            workout.duration ? (
              <div className="mx-4 mt-4 mb-4">
                <div className="bg-white rounded-lg border border-gray-200 p-3 text-center inline-block">
                  <div className="text-xs text-gray-500">Duration</div>
                  <div className="text-lg font-bold">{workout.duration}m</div>
                </div>
              </div>
            ) : null
          )
        ) : (() => {
          const isCardio = (workout.type || workout.workout_type || workout.activityType || workout.activity_type) === 'cardio';
          
          // Hide metrics grid for cardio (header pills replace it)
          if (isCardio) {
            return null;
          }
          
          // Show for strength/HIIT/mobility
          return (
            <div className="mx-4 mb-4">
              <PrimaryMetricsGrid workout={workout} userHrZones={userHrZones} />
            </div>
          );
        })()}

        {/* 🆕 JAN 31, 2026: Equipment Section (moved here, right after metrics) */}
        {(() => {
          // Don't show equipment section for completed workouts (data grid shows everything needed)
          if (workout.status === 'completed' || workout.state === 'completed') {
            return null;
          }
          
          // 🔧 JAN 23, 2026: Show GPS card for outdoor workouts even when equipment is empty
          const isOutdoor = workout.environment === 'outdoor' || 
                          (workout.workout_data?.environment === 'outdoor');
          const gpsRequired = workout.gpsRequired || workout.gps_required || false;
          
          const rawEquipment = workout.equipment || workout.workout_data?.equipment || 
                              workout.workout_data?.equipment_needed || workoutBriefing?.equipment_needed || [];
          const filteredEquipment = rawEquipment.filter((item: string) => 
            item && item.toLowerCase() !== 'none' && item.trim() !== ''
          );
          
          // Show card if: (1) has equipment OR (2) is outdoor GPS workout
          const shouldShowCard = filteredEquipment.length > 0 || (isOutdoor && gpsRequired);
          
          if (!shouldShowCard) return null;
          
          return (
            <div className="mx-4 mb-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                {isOutdoor && gpsRequired && filteredEquipment.length === 0 ? 'GPS Tracking' : 'Equipment Needed'}
              </h3>
              <ul className="space-y-1.5">
                {/* GPS messaging for outdoor workouts with no equipment */}
                {isOutdoor && gpsRequired && filteredEquipment.length === 0 && (
                  <li className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="text-gray-400 mt-0.5">&bull;</span>
                    <span>
                      GPS Tracking
                      <span className="text-xs text-gray-500 ml-1">
                        ({profile?.connectedDevices && profile.connectedDevices.length > 0
                          ? 'Use your connected device'
                          : 'Use Cedar\'s built-in timer with GPS tracking'})
                      </span>
                    </span>
                  </li>
                )}

                {filteredEquipment.map((item: string, index: number) => {
                  const itemLower = item.toLowerCase();
                  const isGPSRelated = itemLower.includes('gps') || itemLower.includes('watch') || itemLower.includes('tracker');
                  const hasSyncedDevice = profile?.connectedDevices && profile.connectedDevices.length > 0;

                  return (
                    <li
                      key={index}
                      className="text-sm text-gray-700 flex items-start gap-2"
                    >
                      <span className="text-gray-400 mt-0.5">&bull;</span>
                      <span>
                        {item}
                        {isGPSRelated && (
                          <span className="text-xs text-gray-500 ml-1">
                            ({hasSyncedDevice
                              ? 'Use your connected device'
                              : 'Use Cedar\'s built-in timer with GPS tracking'})
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })()}

        {/* 🆕 JAN 31, 2026: Main Workout Content Section Starts Here */}
        {/* Workout Details Section - Only show for PRE-completion */}
        {workout.status !== 'completed' && workout.state !== 'completed' && (
          <div className="mb-6">
            {(() => {
              // 🆕 JAN 2026: Defensive check - never show pre-completion content for completed workouts
              if (workout.status === 'completed' || workout.state === 'completed') {
                console.log('⚠️ DEFENSIVE: Skipping pre-completion content for completed workout');
                return null;
              }
              
              const layout = getWorkoutLayout(workout);
              const phases = workout.phases || workout.workout_data?.phases || [];
              
              // FOR INTERVAL/TEMPO WORKOUTS: Show phases using ExpandablePhaseBreakdown
              if ((layout === 'interval' || layout === 'cardio') && phases.length > 0) {
                return (
                  <>
            {/* 🆕 FEB 1, 2026: Warmup mobility expandable (Bug #53 fix) */}
            <ExpandableComponentMobility 
              type="warmup"
              mobilityRoutines={componentMobility}
              onComplete={(routineId) => {
                console.log('Warmup mobility completed:', routineId);
                // TODO: Track completion in backend
              }}
            />
            
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Run Plan</h2>
            </div>
            
            {/* Phase-by-phase breakdown (Task 10: removed blue summary box, phases render individually below) */}
            <div className="space-y-3 mb-6">
              {(() => {
                // Group repeating phases (strides, intervals) for cleaner display
                const groupedPhases = groupRepeatingPhases(phases);
                
                return groupedPhases.map((grouped, idx) => (
                  <CollapsibleRunPhase
                    key={idx}
                    groupedPhase={grouped}
                    index={idx}
                    userHrZones={userHrZones}
                  />
                ));
              })()}
            </div>
            
            {/* 🆕 JAN 31, 2026: Cooldown mobility (inside cardio block) */}
            <ExpandableComponentMobility 
              type="cooldown"
              mobilityRoutines={componentMobility}
              onComplete={(routineId) => {
                console.log('Cooldown mobility completed:', routineId);
                // TODO: Track completion in backend
              }}
            />
                  </>
                );
              }
              
              // 🆕 JAN 29, 2026: COMPLETE STRENGTH UI RESTRUCTURE
              // FOR STRENGTH WORKOUTS: Show phase-based collapsible structure
              if (layout === 'strength' && exercises.length > 0) {
                const phases = workout.workout_data?.phases || workout.phases || [];
                
                return (
                  <>
                    {/* 🆕 FEB 1, 2026: Warmup mobility expandable (Bug #53 fix) */}
                    <ExpandableComponentMobility 
                      type="warmup"
                      mobilityRoutines={componentMobility}
                      onComplete={(routineId) => {
                        console.log('Warmup mobility completed:', routineId);
                        // TODO: Track completion in backend
                      }}
                    />
                    
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-900">Session Plan</h2>
                    </div>
                    
                    {/* 🆕 JAN 29, 2026: DELETE GREEN BOX - Phase descriptions now in phase headers */}
                    {/* OLD: Green "Workout Details" box showing phase list */}
                    {/* NEW: Phase descriptions integrated into ExpandablePhaseSection subtitles */}
                    
                    {/* 🆕 JAN 29, 2026: Phase-Based Collapsible Structure */}
                    <div className="space-y-4">
                      {(() => {
                        const phases = workout.workout_data?.phases || workout.phases || [];
                        
                        // Check if we have phase-nested exercises
                        const hasPhaseExercises = phases.some((p: any) => {
                          const phaseExercises = p.exercises || p.exercises_json || [];
                          return Array.isArray(phaseExercises) && phaseExercises.length > 0;
                        });
                        
                        if (phases.length > 0 && hasPhaseExercises) {
                          // 🎯 NEW: Use ExpandablePhaseSection for collapsible phase-based UI
                          let exerciseCounter = 0;
                          
                          return phases.map((phase: any, phaseIdx: number) => {
                            const phaseExercises = phase.exercises || phase.exercises_json || [];
                            if (!Array.isArray(phaseExercises) || phaseExercises.length === 0) return null;
                            
                            const startingNumber = exerciseCounter + 1;
                            exerciseCounter += phaseExercises.length;
                            
                            return (
                              <ExpandablePhaseSection
                                key={phaseIdx}
                                phase={phase}
                                phaseIndex={phaseIdx}
                                startingExerciseNumber={startingNumber}
                                isDefaultExpanded={phaseIdx === 0} // First phase expanded by default
                              />
                            );
                          });
                        }
                        
                        // Fallback: Show exercises in flat list (legacy/old workouts)
                        return exercises.map((exercise, index) => (
                          <ExerciseCard
                            key={exercise.id}
                            exercise={exercise}
                            index={index}
                            expandedId={expandedExercise}
                            onToggleExpansion={handleExerciseToggle}
                            variant="strength"
                          />
                        ));
                      })()}
                    </div>
                    
                    {/* 🚫 FEB 3, 2026: Hidden - Total volume not meaningful for users or planning
                    {workout.workout_data?.total_volume_kg && typeof workout.workout_data.total_volume_kg === 'number' && (
                      <div className="mt-6">
                        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-5 border border-purple-200">
                          <div className="text-center">
                            <div className="text-sm font-medium text-purple-900 mb-1">Total Estimated Volume</div>
                            <div className="text-3xl font-bold text-purple-900">
                              {workout.workout_data.total_volume_kg.toLocaleString()} kg
                            </div>
                            <div className="text-xs text-purple-700 mt-2">
                              Calculated from all sets × reps × working weight
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    */}
                    
                    {/* 🆕 JAN 31, 2026: Cooldown mobility (inside strength block) */}
                    <ExpandableComponentMobility 
                      type="cooldown"
                      mobilityRoutines={componentMobility}
                      onComplete={(routineId) => {
                        console.log('Cooldown mobility completed:', routineId);
                        // TODO: Track completion in backend
                      }}
                    />
                  </>
                );
              }
              
              // FALLBACK: Simple workout with description (for workouts without phases)
              // 🆕 JAN 22: Show workout description prominently
              return (
                <>
                  {/* 🆕 FEB 1, 2026: Warmup mobility expandable (Bug #53 fix) */}
                  <ExpandableComponentMobility 
                    type="warmup"
                    mobilityRoutines={componentMobility}
                    onComplete={(routineId) => {
                      console.log('Warmup mobility completed:', routineId);
                      // TODO: Track completion in backend
                    }}
                  />
                  
                  <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getTypeColor().replace('text-', 'bg-').replace('-600', '-100')}`}>
                      <div className={getTypeColor()}>
                        {getTypeIcon()}
                      </div>
                    </div>
                    <div className="flex-1">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">Workout Overview</h4>
              {/* 🔧 JAN 22, 2026 SPRINT 3: Personalized coaching description */}
              {(() => {
                // Priority 1: Use LLM-generated coaching overview (when available)
                const coachingOverview = workout.workout_data?.workout_overview_coaching;
                if (coachingOverview) {
                  return (
                    <p className="text-sm text-blue-800 leading-relaxed mb-3">
                      {coachingOverview}
                    </p>
                  );
                }
                
                // Priority 2: Generate smart description from phase data
                if (Array.isArray(workout.phases) && workout.phases.length > 0) {
                  const phases = workout.phases;
                  const workoutType = workout.type || workout.workout_type || '';
                  
                  // Find key phase (not warmup/cooldown)
                  const keyPhase = phases.find((p: any) => 
                    p.phase_type !== 'warmup' && p.phase_type !== 'cooldown'
                  ) || phases[0];
                  
                  const targetPace = keyPhase.target_pace_min_per_km;
                  const phaseType = keyPhase.phase_type || '';
                  
                  // Generate context-aware description
                  if (workoutType.includes('long')) {
                    return (
                      <p className="text-sm text-blue-800 leading-relaxed mb-3">
                        This long run builds endurance with a {phaseType.replace('_', ' ')} section
                        {targetPace ? ` at ${targetPace}/km` : ''}. 
                        Focus on maintaining consistent effort and finishing strong.
                      </p>
                    );
                  }
                  
                  if (workoutType.includes('tempo')) {
                    return (
                      <p className="text-sm text-blue-800 leading-relaxed mb-3">
                        This tempo run targets your lactate threshold
                        {targetPace ? ` at ${targetPace}/km - comfortably hard pace` : ''}. 
                        You're training your body to sustain faster speeds longer.
                      </p>
                    );
                  }
                  
                  if (workoutType.includes('base') || workoutType.includes('easy')) {
                    return (
                      <p className="text-sm text-blue-800 leading-relaxed mb-3">
                        Keep this conversational
                        {targetPace ? ` - ${targetPace}/km target` : ''}. 
                        This builds aerobic capacity. If you can't chat comfortably, ease up.
                      </p>
                    );
                  }
                  
                  if (workoutType.includes('interval')) {
                    return (
                      <p className="text-sm text-blue-800 leading-relaxed mb-3">
                        These intervals target VO2max - building top-end speed. 
                        Hard efforts with full recovery between repeats.
                      </p>
                    );
                  }
                }
                
                // Priority 3: Fallback to generic (old workouts)
                if (workout.description) {
                  return (
                    <p className="text-sm text-blue-800 leading-relaxed mb-3">
                      {workout.description}
                    </p>
                  );
                }
                
                return (
                  <p className="text-sm text-blue-800 leading-relaxed mb-3">
                    Follow the pacing and HR targets for each phase.
                  </p>
                );
              })()}
              
              <div className="flex items-center gap-4 text-xs text-blue-700">
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {String(workout.duration || 0)} min
                </span>
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {String(workout.difficulty || 'moderate')}
                </span>
              </div>
                    </div>
                  </div>
                  
                  {/* 🆕 JAN 31, 2026: Cooldown mobility (inside fallback block) */}
                  <ExpandableComponentMobility 
                    type="cooldown"
                    mobilityRoutines={componentMobility}
                    onComplete={(routineId) => {
                      console.log('Cooldown mobility completed:', routineId);
                      // TODO: Track completion in backend
                    }}
                  />
                </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Brand Attribution Footer - Post-Completion Only */}
        {workout.status === 'completed' && shouldShowDataSources(workout) && getExternalSources(workout).length > 0 && (
          <div className="mt-6">
            <PoweredByAttribution 
              sources={getExternalSources(workout)}
              theme="light"
              layout="auto"
            />
          </div>
        )}
      </div>

      {/* Manual Completion Modal */}
      <ManualCompletionModal
        isOpen={showManualModal}
        workoutTitle={String(workout.title || 'Workout')}
        onClose={() => setShowManualModal(false)}
        onConfirm={async (notes?: string) => {
          try {
            // Mark workout complete with notes
            const result = await DataService.markWorkoutComplete(workout.id, notes);
            if (result?.success) {
              setWorkout((prev: any) => prev ? {
                ...prev,
                status: 'completed',
                completedAt: new Date()
              } : null);

              // Store completion notes for Mission 22 debrief
              if (notes) {
                console.log('💬 Manual completion notes:', notes);
                // Notes will be available for post-workout analysis
              }
            }
            setShowManualModal(false);
          } catch (error) {
            console.error('Error completing workout:', error);
          }
        }}
      />

      {/* 🗑️ JAN 31, 2026: REMOVED DUPLICATE COOLDOWN SECTIONS */}
      {/* Cooldown is already rendered at line 2859-2872 inside workout content */}
      {/* These were duplicate sections causing the double cooldown bug */}

      </div>
      {/* End scrollable content */}

      {/* 🔧 FEB 13: FULLSCREEN TIMER OVERLAY - Renders OUTSIDE scrollable content, fills viewport between header and nav */}
      {showUnifiedTimer && (() => {
        // ✅ FIX: Use getWorkoutLayout() to correctly determine workout type
        const layout = getWorkoutLayout(workout);
        const workoutType = layout === 'interval' || layout === 'cardio' ? 'cardio' :
                           layout === 'strength' ? 'strength' : 'mobility';
        
        // ✅ FIX: Determine activity subtype for GPS detection
        const workoutTypeStr = workout.type || workout.workout_type || '';
        const activitySubtype = workoutTypeStr.includes('running') ? 'running' :
                               workoutTypeStr.includes('cycling') ? 'cycling' :
                               workoutTypeStr.includes('swimming') ? 'swimming' :
                               workoutTypeStr.includes('walking') ? 'walking' :
                               workout.activityType === 'cardio' ? 'running' : 'running'; // Default to running for cardio
        
        // ✅ FIX: Get phases from multiple possible locations + ensure array
        const phasesRaw = workout.phases || 
                         workout.workout_data?.phases || 
                         workout.workoutData?.phases || 
                         [];
        const phases = Array.isArray(phasesRaw) ? phasesRaw : [];
        
        // 🐛 FEB 2, 2026 (Bug #58): Extract exercises safely for timer
        // BUG 3 FIX: Expand circuit/superset phases into repeated exercise sequences
        const CIRCUIT_TYPES = new Set(['circuit', 'superset', 'emom', 'amrap']);
        const rawExercises = workout.exercises || workout.workout_data?.exercises || [];
        const hasCircuits = phases.some((p: any) => CIRCUIT_TYPES.has((p.phase_type || '').toLowerCase()));
        const hasRepeatingPhases = phases.some((p: any) => (p.repeat_count ?? 0) > 1);
        let timerExercises: any[];

        if (rawExercises.length > 0 && !hasCircuits && !hasRepeatingPhases) {
          timerExercises = rawExercises.map((ex: any) => ({
            id: ex.id, name: ex.name, description: ex.description, duration: ex.duration,
            sets: ex.sets, reps: ex.repetitions || ex.reps, instructions: ex.instructions,
            form_cues: ex.form_cues, safety_notes: ex.safety_notes,
            images: ex.images || ex.media_assets,
          }));
        } else {
          timerExercises = [];
          phases.forEach((phase: any) => {
            const phaseType = (phase.phase_type || '').toLowerCase();
            const phaseExercises = phase.exercises_json || phase.exercises || [];
            if (phaseExercises.length === 0) return;
            const isCircuit = CIRCUIT_TYPES.has(phaseType);
            let totalRounds = 1;
            // Extract round count from repeat_count (explicit) or uniform sets (implicit)
            if (phase.repeat_count && phase.repeat_count > 1) {
              totalRounds = phase.repeat_count;
            } else if (isCircuit) {
              const sv = phaseExercises.map((e: any) => e.sets || 1);
              if (sv.every((s: number) => s === sv[0]) && sv[0] > 1) totalRounds = sv[0];
            }
            if (totalRounds > 1) {
              const total = phaseExercises.length;
              for (let r = 1; r <= totalRounds; r++) {
                phaseExercises.forEach((ex: any, i: number) => {
                  // For timed circuit exercises (duration, no reps), sets=1 because rounds handle repetition.
                  // For rep-based exercises in a repeating phase, preserve original sets.
                  const exReps = ex.repetitions || ex.reps;
                  const isTimed = ex.duration && (!exReps || exReps === 0);
                  timerExercises.push({
                    id: ex.id || `p${phase.phase_order}-r${r}-e${i}`, name: ex.name,
                    description: ex.description, duration: ex.duration, sets: isTimed ? 1 : (ex.sets || 1),
                    reps: exReps, instructions: ex.instructions,
                    form_cues: ex.form_cues, safety_notes: ex.safety_notes,
                    images: ex.images || ex.media_assets,
                    circuit_info: {
                      circuit_number: phase.phase_order || 1, round_number: r,
                      exercise_in_circuit: i + 1, total_exercises_in_circuit: total,
                      total_rounds: totalRounds, is_round_end: i === total - 1,
                      is_circuit_end: r === totalRounds && i === total - 1,
                    },
                  });
                });
              }
            } else {
              phaseExercises.forEach((ex: any, i: number) => {
                timerExercises.push({
                  id: ex.id || `p${phase.phase_order}-e${i}`, name: ex.name,
                  description: ex.description, duration: ex.duration, sets: ex.sets,
                  reps: ex.repetitions || ex.reps, instructions: ex.instructions,
                  form_cues: ex.form_cues, safety_notes: ex.safety_notes,
                  images: ex.images || ex.media_assets,
                });
              });
            }
          });
          if (timerExercises.length === 0) {
            timerExercises = rawExercises.map((ex: any) => ({
              id: ex.id, name: ex.name, description: ex.description, duration: ex.duration,
              sets: ex.sets, reps: ex.repetitions || ex.reps, instructions: ex.instructions,
              form_cues: ex.form_cues, safety_notes: ex.safety_notes,
              images: ex.images || ex.media_assets,
            }));
          }
        }
        
        console.log('🔍 WorkoutDetail: Timer props', {
          workoutId: workout.id,
          workoutTitle: String(workout.title || 'Workout'),
          workoutType,
          activitySubtype,
          layout,
          phasesCount: phases.length,
          phases: phases,
          exercisesCount: timerExercises.length,
          environment: typeof workout.environment === 'string' ? workout.environment : 'outdoor',
          activityType: typeof workout.activityType === 'string' ? workout.activityType : undefined,
          type: typeof workout.type === 'string' ? workout.type : String(workout.type || 'cardio')
        });
        
        return (
          <div style={{
            flex: 1,
            minHeight: 0,
            zIndex: 40,
            backgroundColor: '#111827',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            paddingBottom: 'calc(82px + env(safe-area-inset-bottom, 0px))' // Account for fixed bottom nav
          }}>
            <UnifiedWorkoutTimer
              workoutId={workout.id}
              workoutTitle={String(workout.title || 'Workout')}
              workoutType={workoutType}
              activitySubtype={activitySubtype} // ✅ FIX: Pass activity subtype for GPS
              environment={typeof workout.environment === 'string' ? workout.environment : 'outdoor'} // 🗺️ GPS TRACKING: Pass environment for GPS logic
              gpsRequired={typeof workout.gpsRequired === 'boolean' ? workout.gpsRequired : (workoutType === 'cardio' && workout.environment === 'outdoor')} // ✅ FIX: Auto-detect GPS requirement
              exercises={timerExercises}
              phases={phases} // ✅ FIX: Pass phases from all possible locations
              plannedDistance={typeof workout.plannedDistance === 'number' ? workout.plannedDistance : undefined}
              onComplete={handleSessionComplete} // 🔧 FEB 13: Wire to full session completion + debrief pipeline (not manual modal)
              onDiscard={() => {
                console.log('Workout discarded');
                setShowUnifiedTimer(false);
              }}
              onExit={() => {
                console.log('Timer closed');
                setShowUnifiedTimer(false);
              }}
              componentMobility={componentMobility} // 🆕 PHASE 3: Pass warmup/cooldown for completion flow
              onMoveToCooldown={(cooldownId) => { // 🆕 PHASE 3: Navigate to cooldown
                console.log('❄️ MOVING TO COOLDOWN: Navigating to post-workout cooldown');
                // 🚨 CRITICAL FIX (Feb 3, 2026): Correct route is /mobility/:id NOT /mobility-detail/:id
                // Bug: Wrong route caused crash to chat on second workout visit!
                router.push(`/mobility/${cooldownId}?source=post-workout&parentWorkoutId=${workout.id}`);
              }}
            />
          </div>
        );
      })()}

      {/* 🔧 JAN 23, 2026: Action Buttons - OUTSIDE scrolling container (sibling, not child) */}
      {/* Pre-Completion Only (Fixed at bottom, above navigation) */}
      {(() => {
        // 🆕 JAN 5, 2026: Only check status field (not state - that's a database inconsistency)
        // Root cause: DB has both 'state' and 'status' columns, but frontend only uses 'status'
        const isCompleted = workout?.status === 'completed' || workout?.completedAt;
        // 🐛 BUG #82 FIX (Issue 3): Hide button when timer is active
        const shouldShowButtons = workout && !isCompleted && !showUnifiedTimer;
        
        console.log('🔘 Action Buttons Check:', {
          hasWorkout: !!workout,
          status: workout?.status,
          completedAt: workout?.completedAt,
          isCompleted,
          showUnifiedTimer,
          shouldShowButtons,
          workoutId: workout?.id
        });
        
        return shouldShowButtons;
      })() && (
        <div 
          className="fixed left-1/2 transform -translate-x-1/2 bg-white px-4 pt-3 pb-0"
          style={{ 
            position: 'fixed', // 🔧 JAN 22 SPRINT 3: Explicit fixed position (no class conflicts)
            bottom: 'calc(82px + env(safe-area-inset-bottom, 0px))', // Just above 82px navigation - no gap
            width: '100%',
            maxWidth: '448px',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
            zIndex: 40 // Below navigation (z-50) but above content
          }}
        >
          {/* 🔧 FEB 1, 2026: CORRECTED - Single "Start Session" button only */}
          {/* Manual completion via header icon click (not separate button) */}
          <button
            onClick={handleStartSession}
            className="w-full bg-[#1F6C4F] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#1F4524] transition-colors flex items-center justify-center gap-2 shadow-lg mb-3"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Start Session
          </button>
        </div>
      )}

      {/* 🆕 JAN 22, 2026: Fixed Bottom Navigation - matches MyPath.tsx pattern */}
      <div 
        className="navigation-container fixed bottom-0 left-1/2 transform -translate-x-1/2 bg-white border-t border-gray-200 z-50"
        style={{ width: '100%', maxWidth: '448px' }}
      >
        <ProperNavCoach 
          activeTab="mypath"
          className=""
          onClickTab={handleNavigation}
        />
      </div>

      {/* Manual Completion Modal - Sprint 3 Task 3.1 */}
      <ManualCompletionModal
        isOpen={showManualCompletionModal}
        onClose={() => setShowManualCompletionModal(false)}
        onConfirm={handleManualCompletionConfirm}
        workoutTitle={String(workout?.title || 'Workout')}
      />
    </div>
  );
};

export default WorkoutDetail;
